import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import FormatListNumberedRoundedIcon from '@mui/icons-material/FormatListNumberedRounded';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SearchIcon from '@mui/icons-material/Search';
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Popover,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import SortIcon from '@mui/icons-material/Sort';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  useFollowUpTracker,
  type FollowUpAccount,
  type FollowUpBucket,
  type FollowUpMonthlyVolume,
} from '../api/depletions';
import { BUCKET_META, BUCKET_ORDER, SHORT_BUCKET_LABEL } from '../lib/followUpBuckets';
import { lacksCoreWhiskey } from '../lib/coreWhiskey';
import { PremiumPagination } from './PremiumPagination';
import { StateFilterSelect } from './StateFilterSelect';
import { colors } from '../theme';
import { fmt6PkNumber } from '../lib/volume';
import { parseBackendDate } from '../util/dates';
import { PremisesBadge } from './PremisesBadge';
import { PREMISES_STYLE } from './premisesStyle';
import { SectionExplainer, type SectionExplainerRow } from './SectionExplainer';
import { useSectionExport } from '../hooks/useSectionExport';
import {
  accountColumns,
  excelSheet,
  isoDate,
  num,
  premisesLabel,
  type ExcelWorkbookSpec,
  type ExportFilter,
} from '../util/excel-export';

// Bucket color palette is centralized in theme.ts.  We extend it
// with a brand-gold accent for the synthetic "Today's Plan" bucket
// the Field Sales surface passes in — the default chartPrimary
// indigo doesn't match HY's gold/cream palette.
const BUCKET_COLORS: Record<string, string> = {
  ...colors.bucketColors,
  "Today's Plan": colors.gold,
};

const fmt = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });

function formatRef(iso: string): string {
  const d = parseBackendDate(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

type VelocityFilter = 'all' | 'growing' | 'declining' | 'steady' | 'new';
type SortBy = 'rank' | 'volume_desc' | 'velocity_desc' | 'velocity_asc' | 'recent_first';

const VELOCITY_FILTER_LABEL: Record<VelocityFilter, string> = {
  all: 'All',
  growing: 'Growing',
  declining: 'Declining',
  steady: 'Steady',
  new: 'New activity',
};

const SORT_LABEL: Record<SortBy, string> = {
  rank: 'Days since (default)',
  recent_first: 'Most recent first',
  volume_desc: 'Highest 6-pk first',
  velocity_desc: 'Biggest growers first',
  velocity_asc: 'Biggest decliners first',
};

// Export Info wording: names the measure and its direction.
const SORT_EXPORT_LABEL: Record<SortBy, string> = {
  rank: 'Default (missing Rye/Bourbon first, then days since last order)',
  recent_first: 'Days since last order (low → high)',
  volume_desc: 'Volume (high → low)',
  velocity_desc: 'Momentum (high → low)',
  velocity_asc: 'Momentum (low → high)',
};

// ----------------------------------------------------------------
// Action flags — strategic signals derived per account on top of the
// dormancy bucketing. The Follow-Up Tracker is now the single hub
// for "what should I do today?" — buckets answer "who has gone
// quiet" and these chips answer "what kind of call is this?".
// ----------------------------------------------------------------

type ActionFlag = 'core_gap' | 'mono_sku' | 'fresh' | 'at_risk' | 'recovery' | 'defend';

// Burnt-orange tone reserved for the core-whiskey gap so it reads as a
// distinct top-priority opportunity, not a risk (red) or growth (green).
const CORE_GAP_TONE = '#9a3412';

const ACTION_LABEL: Record<ActionFlag, string> = {
  core_gap: 'No Rye/Bourbon',
  mono_sku: 'Cross-sell',
  fresh: 'Nurture',
  at_risk: 'At-risk',
  recovery: 'Recovery',
  defend: 'Defend',
};

const ACTION_DESCRIBE: Record<ActionFlag, string> = {
  core_gap: "Doesn't carry both a Rye and a Bourbon. Pitch the core whiskey line.",
  mono_sku: 'Account buys only one SKU. Add a second reason to call.',
  fresh: 'First active in the last 90 days. Lock in the habit.',
  at_risk: 'Was a near-monthly buyer; recent volume dropped sharply.',
  recovery: 'Had a ≥4-month streak; currently silent. Reconnect.',
  defend: 'Recent 3M is up sharply vs prior 3M. Lock in the momentum.',
};

const ACTION_STYLE: Record<ActionFlag, { color: string; bg: string }> = {
  core_gap: { color: CORE_GAP_TONE, bg: `${CORE_GAP_TONE}14` },
  mono_sku: { color: colors.info, bg: `${colors.info}14` },
  fresh: { color: colors.success, bg: `${colors.success}14` },
  at_risk: { color: colors.error, bg: `${colors.error}14` },
  recovery: { color: colors.warning, bg: `${colors.warning}14` },
  defend: { color: colors.chartPrimary, bg: `${colors.chartPrimary}14` },
};

// Core-whiskey gap leads — HY wants those accounts pitched first. Then
// red/loss signals, the growth-defence call, amber recovery, and
// expansion / onboarding.
const ACTION_ORDER: ActionFlag[] = [
  'core_gap',
  'at_risk',
  'defend',
  'recovery',
  'mono_sku',
  'fresh',
];

interface AccountActionFlags {
  core_gap: boolean;
  mono_sku: boolean;
  fresh: boolean;
  at_risk: boolean;
  recovery: boolean;
  defend: boolean;
}

function deriveActionFlags(acc: FollowUpAccount, referenceDate: string | null): AccountActionFlags {
  const vals = acc.monthly_series.map((p) => Number(p.cases_9l));
  const monthsActive = vals.filter((v) => v > 0).length;

  // Mono-SKU — exactly one distinct product across all history.
  const monoSku = acc.product_count === 1;

  // Fresh — first ever non-zero month was within the last 90 days
  // relative to the reference date. Uses customer_since (already
  // computed server-side).
  let fresh = false;
  if (acc.customer_since && referenceDate) {
    const days =
      (new Date(`${referenceDate}T00:00:00Z`).getTime() -
        new Date(`${acc.customer_since}T00:00:00Z`).getTime()) /
      (1000 * 60 * 60 * 24);
    fresh = days >= 0 && days <= 90;
  }

  // At-risk — was a Monthly-tier buyer (≥9 of last 12 months active)
  // AND recent 3M dropped sharply vs prior 3M. Catches the slip
  // before it shows up as 90+ days dormant.
  const recent = Number(acc.recent_3m_9l);
  const prior = Number(acc.prior_3m_9l);
  const sharpDrop =
    (acc.velocity_pct !== null && acc.velocity_pct <= -30) || (prior > 0 && recent < prior * 0.5);
  const atRisk = monthsActive >= 9 && sharpDrop;

  // Recovery — had at least one ≥4-month streak historically and is
  // currently silent (current streak is zero).
  let longestStreak = 0;
  let running = 0;
  for (const v of vals) {
    if (v > 0) {
      running++;
      if (running > longestStreak) longestStreak = running;
    } else {
      running = 0;
    }
  }
  let currentStreak = 0;
  for (let i = vals.length - 1; i >= 0; i--) {
    if (vals[i]! > 0) currentStreak++;
    else break;
  }
  const recovery = longestStreak >= 4 && currentStreak === 0;

  // Defend — recent 3M is up sharply vs prior 3M. Sharp enough to be
  // a real trend (≥30% growth), with a meaningful baseline so we
  // don't flag tiny accounts that doubled from 1 9L to 2 9L. Mutually
  // exclusive with at-risk, which catches the opposite direction.
  const sharpGrowth =
    (acc.velocity_pct !== null && acc.velocity_pct >= 30) || (prior > 0 && recent >= prior * 1.5);
  const defend = !atRisk && prior >= 5 && recent >= 5 && sharpGrowth;

  // Core gap — doesn't carry both a Rye and a Bourbon (Bottled in Bond).
  const coreGap = lacksCoreWhiskey(acc.products);

  return { core_gap: coreGap, mono_sku: monoSku, fresh, at_risk: atRisk, recovery, defend };
}

type Tier = 'TOP' | 'BIG' | 'MID';

const TIER_STYLE: Record<Tier, { color: string; bg: string; border: string; tip: string }> = {
  TOP: {
    color: '#a67100',
    bg: 'rgba(212, 168, 75, 0.16)',
    border: 'rgba(212, 168, 75, 0.4)',
    tip: 'Top 10% by lifetime 6-pk in this bucket — your highest-leverage call.',
  },
  BIG: {
    color: '#4f46e5',
    bg: 'rgba(79, 70, 229, 0.10)',
    border: 'rgba(79, 70, 229, 0.3)',
    tip: 'Top 25% by lifetime 6-pk in this bucket — a meaningful account worth real attention.',
  },
  MID: {
    color: '#475569',
    bg: 'rgba(71, 85, 105, 0.08)',
    border: 'rgba(71, 85, 105, 0.25)',
    tip: 'Top 50% by lifetime 6-pk in this bucket — a steady contributor.',
  },
};

// Numbered step badge used in the "How accounts are ranked" explainer.
function RankBadge({ n, color = colors.chartPrimary }: { n: number; color?: string }) {
  return (
    <Box
      sx={{
        width: 22,
        height: 22,
        borderRadius: '50%',
        bgcolor: `${color}14`,
        color,
        border: `1px solid ${color}33`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        fontWeight: 800,
      }}
    >
      {n}
    </Box>
  );
}

// ---- "Ranking criteria" popover content -------------------------------
// Structured, in the order the system applies it. Kept in lock-step with
// the logic below: bucketing (backend get_follow_up_tracker), tiers
// (tierByAccount), momentum (velocity_pct), flags (computeFlags), and
// action (suggestedAction).

// Small rounded label used for buckets, momentum states, and flags.
function CritPill({ label, color }: { label: string; color: string }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        px: 0.85,
        py: 0.25,
        borderRadius: 999,
        fontSize: 10.5,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        color,
        bgcolor: `${color}14`,
        border: `1px solid ${color}33`,
      }}
    >
      {label}
    </Box>
  );
}

// Reuses the real tier-badge styling from the table.
function TierPill({ tier }: { tier: Tier }) {
  const s = TIER_STYLE[tier];
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        px: 0.7,
        py: 0.2,
        borderRadius: 0.75,
        fontSize: 10,
        fontWeight: 800,
        letterSpacing: '0.04em',
        color: s.color,
        bgcolor: s.bg,
        border: `1px solid ${s.border}`,
      }}
    >
      {tier}
    </Box>
  );
}

function CritLead({ children }: { children: ReactNode }) {
  return (
    <Typography
      sx={{
        fontSize: 11.5,
        color: colors.textMuted,
        lineHeight: 1.45,
        '& strong': { color: colors.textSecondary, fontWeight: 700 },
      }}
    >
      {children}
    </Typography>
  );
}

// A label → rule row with a fixed badge column so the rules line up.
function CritDefRow({ badge, children }: { badge: ReactNode; children: ReactNode }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
      <Box sx={{ flexShrink: 0, minWidth: 86, display: 'flex' }}>{badge}</Box>
      <Typography
        sx={{
          fontSize: 11.5,
          color: colors.textMuted,
          lineHeight: 1.4,
          '& strong': { color: colors.textSecondary, fontWeight: 700 },
        }}
      >
        {children}
      </Typography>
    </Box>
  );
}

function ChipRow({ children }: { children: ReactNode }) {
  return <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>{children}</Box>;
}

function CritSection({
  n,
  title,
  accent,
  children,
}: {
  n: number;
  title: string;
  accent?: string;
  children: ReactNode;
}) {
  return (
    <Box sx={{ display: 'flex', gap: 1.25 }}>
      <Box sx={{ flexShrink: 0, mt: 0.1 }}>
        <RankBadge n={n} color={accent} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          sx={{
            fontSize: 12.5,
            fontWeight: 700,
            color: colors.textPrimary,
            lineHeight: 1.3,
            mb: 0.65,
          }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>{children}</Box>
      </Box>
    </Box>
  );
}

const RECENCY_BUCKETS = ['0–30', '30–60', '60–90', '90–120', '120+'];

function RankingCriteriaBody() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.9 }}>
      <CritSection n={1} title="Grouped by recency">
        <CritLead>
          Each account lands in a bucket by{' '}
          <strong>days since its last non-zero depletion month</strong>. The tab you pick sets which
          group you’re viewing.
        </CritLead>
        <ChipRow>
          {RECENCY_BUCKETS.map((b) => (
            <CritPill key={b} label={`${b} days`} color={colors.chartPrimary} />
          ))}
        </ChipRow>
      </CritSection>

      <CritSection n={2} title="Ordered within a bucket">
        <CritLead>
          Default order is <strong>days since last order</strong> — most-recently-active first. Use{' '}
          <strong>Sort</strong> to switch to lifetime volume or momentum (fastest-rising /
          fastest-falling).
        </CritLead>
      </CritSection>

      <CritSection n={3} title="Priority tier">
        <CritLead>
          Inside each bucket, accounts are ranked by <strong>lifetime volume</strong>:
        </CritLead>
        <CritDefRow badge={<TierPill tier="TOP" />}>
          Top 10% — your highest-leverage accounts
        </CritDefRow>
        <CritDefRow badge={<TierPill tier="BIG" />}>Top 25% — worth real attention</CritDefRow>
        <CritDefRow badge={<TierPill tier="MID" />}>Top 50% — steady contributors</CritDefRow>
      </CritSection>

      <CritSection n={4} title="Momentum">
        <CritLead>
          Compares the <strong>last 3 months vs the prior 3 months</strong>:
        </CritLead>
        <ChipRow>
          <CritPill label="Growing ≥ +10%" color={colors.success} />
          <CritPill label="Declining ≤ −10%" color={colors.error} />
          <CritPill label="Steady" color={colors.textMuted} />
        </ChipRow>
      </CritSection>

      <CritSection n={5} title="Auto-flags">
        <CritLead>
          Pattern detectors that surface accounts needing attention. <strong>No Rye/Bourbon</strong>{' '}
          sorts these accounts to the top of the default view.
        </CritLead>
        <CritDefRow badge={<CritPill label="No Rye/Bourbon" color={CORE_GAP_TONE} />}>
          doesn’t carry both a Rye and a Bourbon — pitch the core line
        </CritDefRow>
        <CritDefRow badge={<CritPill label="At-risk" color={colors.error} />}>
          near-monthly buyer that just dropped ≥ 30%
        </CritDefRow>
        <CritDefRow badge={<CritPill label="Recovery" color={colors.info} />}>
          had a 4+ month run, now silent
        </CritDefRow>
        <CritDefRow badge={<CritPill label="Defend" color={colors.success} />}>
          up ≥ 30% off a real base
        </CritDefRow>
        <CritDefRow badge={<CritPill label="New" color={colors.chartPrimary} />}>
          first activity within 90 days
        </CritDefRow>
        <CritDefRow badge={<CritPill label="Mono-SKU" color={colors.warning} />}>
          buys one product only
        </CritDefRow>
      </CritSection>

      <CritSection n={6} title="Suggested action">
        <CritLead>
          Each row’s action chip is chosen from its <strong>bucket + momentum</strong>:
        </CritLead>
        <CritDefRow badge={<CritPill label="60–90 days" color={colors.chartPrimary} />}>
          → <strong>Re-engage call</strong>
        </CritDefRow>
        <CritDefRow badge={<CritPill label="120+ days" color={colors.error} />}>
          no new orders → <strong>Risk escalation</strong>
        </CritDefRow>
      </CritSection>
    </Box>
  );
}

/**
 * Reusable "Ranking Criteria" header control: a compact pill that opens a
 * popover explaining how a list is ranked. Accent-aware so it can match the
 * surface it sits on (indigo dashboard / gold field). Its own click +
 * keydown stopPropagation so it never toggles a surrounding collapse header.
 */
function RankingInfoButton({
  label = 'Ranking Criteria',
  tooltip = 'How accounts are ranked',
  title = 'Ranking criteria',
  accent = colors.chartPrimary,
  children,
}: {
  label?: string;
  tooltip?: string;
  title?: string;
  accent?: string;
  children: ReactNode;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  return (
    <>
      <Tooltip title={tooltip} arrow placement="top">
        <Box
          component="button"
          type="button"
          aria-label={tooltip}
          onClick={(e) => {
            e.stopPropagation();
            setAnchorEl(anchorEl ? null : e.currentTarget);
          }}
          onKeyDown={(e) => e.stopPropagation()}
          sx={{
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.5,
            height: 30,
            px: 1.1,
            border: `1px solid ${open ? `${accent}55` : `${accent}26`}`,
            background: open ? `${accent}1f` : `${accent}0d`,
            color: accent,
            cursor: 'pointer',
            borderRadius: 999,
            transition: 'background 0.15s, border-color 0.15s',
            '&:hover': { background: `${accent}1f`, borderColor: `${accent}55` },
            '&:focus-visible': { outline: `2px solid ${accent}`, outlineOffset: 2 },
          }}
        >
          <FormatListNumberedRoundedIcon sx={{ fontSize: 17 }} />
          <Box
            component="span"
            sx={{
              display: { xs: 'none', md: 'inline' },
              fontSize: 12,
              fontWeight: 600,
              color: accent,
            }}
          >
            {label}
          </Box>
        </Box>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              p: 2,
              maxWidth: 460,
              width: 'calc(100vw - 32px)',
              borderRadius: 2,
              border: `1px solid ${colors.border}`,
              boxShadow: '0 12px 32px -12px rgba(15,23,42,0.25)',
            },
          },
        }}
      >
        <Typography
          sx={{
            fontSize: 12,
            fontWeight: 800,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: colors.textPrimary,
            mb: 1.25,
          }}
        >
          {title}
        </Typography>
        {children}
      </Popover>
    </>
  );
}

/**
 * Dashboard Follow-Up Tracker ranking control — standard recency-bucket
 * criteria. Rendered in the collapsible section header (visible collapsed).
 */
export function FollowUpRankingInfo() {
  return (
    <RankingInfoButton tooltip="How accounts are ranked" title="Ranking criteria">
      <RankingCriteriaBody />
    </RankingInfoButton>
  );
}

// Field "Today's Plan" ranks by a different model (todayScore): pinned →
// follow-up urgency → momentum/new boosts → proximity to home → lifetime,
// with a recent-visit cooldown. This body mirrors that so the field control
// reflects the actual list, not the dashboard buckets.
const FIELD_GOLD = '#bb8c3f';

function TodaysPlanRankingBody() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.9 }}>
      <CritSection n={1} title="Pinned first" accent={FIELD_GOLD}>
        <CritLead>
          Accounts you <strong>pin</strong> always land at the very top of today’s list.
        </CritLead>
      </CritSection>

      <CritSection n={2} title="Missing Rye or Bourbon" accent={FIELD_GOLD}>
        <CritLead>
          Accounts that don’t carry <strong>both a Rye and a Bourbon</strong> (Bottled in Bond) are
          bumped to the top so the core whiskey line gets pitched first.
        </CritLead>
        <ChipRow>
          <CritPill label="No Rye/Bourbon" color={CORE_GAP_TONE} />
        </ChipRow>
      </CritSection>

      <CritSection n={3} title="Follow-up urgency" accent={FIELD_GOLD}>
        <CritLead>
          Then by how time-sensitive the follow-up is — the <strong>1–2 month</strong> re-order
          window scores highest, then longer gaps:
        </CritLead>
        <ChipRow>
          <CritPill label="Check-In · 1–2 mo" color="#326eb8" />
          <CritPill label="Re-Engage · 2–3 mo" color="#a86b1e" />
          <CritPill label="Win-Back · 3–4 mo" color="#b25a1e" />
          <CritPill label="Nurture · <30d" color="#22865a" />
          <CritPill label="At-Risk · 4+ mo" color="#a13a3a" />
        </ChipRow>
      </CritSection>

      <CritSection n={4} title="Signal boosts" accent={FIELD_GOLD}>
        <CritLead>Accounts moving in a way that needs a touch get bumped up:</CritLead>
        <CritDefRow badge={<CritPill label="Declining" color="#a13a3a" />}>
          recent 3-month volume down ≥ 10% — biggest bump
        </CritDefRow>
        <CritDefRow badge={<CritPill label="New activity" color="#326eb8" />}>
          first orders just landed, or a dormant account resumed
        </CritDefRow>
        <CritDefRow badge={<CritPill label="Growing" color="#22865a" />}>
          up ≥ 10% — a smaller nudge
        </CritDefRow>
      </CritSection>

      <CritSection n={5} title="Near you" accent={FIELD_GOLD}>
        <CritLead>Accounts close to your home rank higher so a day routes efficiently:</CritLead>
        <CritDefRow badge={<CritPill label="In your ZIP" color={FIELD_GOLD} />}>
          same 5-digit ZIP as your home address
        </CritDefRow>
        <CritDefRow badge={<CritPill label="Near you" color={FIELD_GOLD} />}>
          same metro (ZIP-3 sectional center)
        </CritDefRow>
      </CritSection>

      <CritSection n={6} title="Lifetime volume" accent={FIELD_GOLD}>
        <CritLead>
          Between otherwise similar accounts, bigger <strong>lifetime buyers</strong> get a nudge
          up.
        </CritLead>
      </CritSection>

      <CritSection n={7} title="Recent-visit cooldown" accent={FIELD_GOLD}>
        <CritLead>
          Visited an account in the <strong>last ~3 days</strong>? It’s suppressed from today’s
          picks so you’re not sent back to a buyer you just saw.
        </CritLead>
      </CritSection>
    </Box>
  );
}

/**
 * Field "Today's Plan" ranking control — mirrors todayScore, in the field's
 * gold accent. Rendered in the Today's Plan header (see FieldPage).
 */
export function TodaysPlanRankingInfo() {
  return (
    <RankingInfoButton
      tooltip="How today's picks are chosen"
      title="How today's picks are chosen"
      accent={FIELD_GOLD}
    >
      <TodaysPlanRankingBody />
    </RankingInfoButton>
  );
}

// Compute whole months between two YYYY-MM-DD ISO strings.
function monthsBetween(fromIso: string, toIso: string): number {
  const [fy, fm] = fromIso.split('-').map(Number);
  const [ty, tm] = toIso.split('-').map(Number);
  if (!fy || !fm || !ty || !tm) return 0;
  return Math.max(0, (ty - fy) * 12 + (tm - fm));
}

// Format a YYYY-MM-DD as e.g. "Sep '25".
function shortMonth(iso: string): string {
  const [y, m] = iso.split('-');
  const idx = m ? Number(m) - 1 : 0;
  const names = [
    'Jan',
    'Feb',
    'Mar',
    'Apr',
    'May',
    'Jun',
    'Jul',
    'Aug',
    'Sep',
    'Oct',
    'Nov',
    'Dec',
  ];
  return `${names[idx] ?? '?'} '${(y ?? '').slice(-2)}`;
}

// ----------------------------------------------------------------
// Suggested next action — opinionated rule table mapping
// (bucket, velocity, new-activity) → a single chip telling the rep
// what to do next. Designed to turn the table from descriptive into
// prescriptive without requiring the rep to interpret the velocity %.
// ----------------------------------------------------------------

interface SuggestedAction {
  label: string;
  color: string;
  bg: string;
  why: string;
}

function suggestedAction(
  bucket: string,
  velocityPct: number | null,
  recent: number,
  prior: number,
): SuggestedAction {
  const isNewActivity = prior === 0 && recent > 0;
  const isGrowing = velocityPct !== null && velocityPct >= 10;
  const isDeclining = velocityPct !== null && velocityPct <= -10;

  // 0-30 days — recent orderers. Defaults to "Thank-you note"; growers
  // get a "Reorder upsell" nudge.
  if (bucket === '0-30 days') {
    if (isGrowing) {
      return {
        label: 'Reorder upsell',
        color: colors.success,
        bg: `${colors.success}14`,
        why: 'Recent orderer trending up — suggest the next SKU or a case-deal upsell.',
      };
    }
    return {
      label: 'Thank-you',
      color: colors.info,
      bg: `${colors.info}14`,
      why: 'Recently ordered — send a thank-you, reinforce the relationship.',
    };
  }

  // 30-60 days — likely running low. Push for the reorder, or refresh
  // their attention if they're slowing down.
  if (bucket === '30-60 days') {
    if (isDeclining) {
      return {
        label: 'Catalog refresh',
        color: colors.warning,
        bg: `${colors.warning}14`,
        why: 'Order pattern is fading — share new releases or seasonal promos to re-spark interest.',
      };
    }
    if (isGrowing) {
      return {
        label: 'Confirm supply',
        color: colors.success,
        bg: `${colors.success}14`,
        why: 'Trending up and getting close to reorder — confirm inventory is set so the next order doesn’t stall.',
      };
    }
    return {
      label: 'Check in',
      color: colors.info,
      bg: `${colors.info}14`,
      why: 'Last order was 1–2 months ago — light availability nudge usually prompts a reorder.',
    };
  }

  // 60-90 days — slowed clearly. Time for a conversation.
  if (bucket === '60-90 days') {
    return {
      label: 'Re-engage call',
      color: colors.chartPrimary,
      bg: `${colors.chartPrimary}14`,
      why: 'Two+ months silent — schedule a call, share new releases, or set up a tasting.',
    };
  }

  // 90-120 days — going cold. Incentive territory.
  if (bucket === '90-120 days') {
    return {
      label: 'Win-back',
      color: '#f97316',
      bg: '#f9731614',
      why: 'Approaching dormant — offer incentives or case deals, find out what stalled.',
    };
  }

  // 120+ days. New activity = re-engaged. Otherwise risk.
  if (bucket === '120+ days') {
    if (isNewActivity) {
      return {
        label: 'Welcome back',
        color: colors.chartPrimary,
        bg: `${colors.chartPrimary}14`,
        why: 'Dormant account that just started ordering again — nurture this fresh re-engagement.',
      };
    }
    return {
      label: 'Risk escalation',
      color: colors.error,
      bg: `${colors.error}14`,
      why: 'No orders in 4+ months — escalate; needs urgent outreach before the relationship is lost.',
    };
  }

  return {
    label: 'Review',
    color: colors.textMuted,
    bg: colors.surfaceSubtle,
    why: 'No automatic recommendation matched — review manually.',
  };
}

interface FollowUpTrackerProps {
  /** Replace the default Excel export. Receives every row the tracker is
   *  showing (filters + sort applied, all pages) in on-screen order, plus
   *  the tracker's own applied filters for the Export Info sheet — lets a
   *  host surface like Today's Plan shape its own visit sheet. */
  buildExport?: (rows: FollowUpAccount[], filters: ExportFilter[]) => ExcelWorkbookSpec | null;
  /** Optional override for the data source.  Used by the Field Sales
   *  surface to pass in a territory-scoped follow-up query instead of
   *  the default global depletions view. */
  useDataSource?: typeof useFollowUpTracker;
  /** Optional renderer for an extra trailing column per row.  Used
   *  by the Field Sales surface to slot in pin / log-visit / last-
   *  visit chips alongside each account without forking this
   *  component.  When set, the table grows by one column. */
  rowActions?: (account: FollowUpAccount) => ReactNode;
  /** Extra eyebrow text rendered in the table header for the trailing
   *  rep-actions column. */
  rowActionsHeader?: string;
  /** Header tooltip body for the rep-actions column. Defaults to the
   *  Today's Plan wording (pin / log / last contact). */
  rowActionsDescription?: ReactNode;
  /** Bucket label to select on first render.  Falls back to the
   *  first bucket present in the data when the requested label
   *  doesn't exist (e.g. the synthetic "Today's Plan" bucket the
   *  Field Sales surface passes in). */
  defaultBucket?: string;
  /** Hide the inner "Follow-Up Tracker" icon + title + caption
   *  block at the top.  Used by the Field Sales surface where the
   *  page-level H1 ("Your Sales Floor") and Territory panel already
   *  set context, so the inner header would be a duplicate. */
  hideHeading?: boolean;
  /** Hide the Action / Momentum / Premises chip-filter row above
   *  the table.  Used by the Today's Plan tab — the list there is
   *  already curated by `todayScore`, so re-filtering would just
   *  subvert the system's picks.  Manual filtering belongs on the
   *  My Territories tab. */
  hideFilterChips?: boolean;
  /** Optional per-row style hook.  Returns extra `sx` properties
   *  merged into the TableRow.  Used by the Today's Plan tab to
   *  fade rows whose visit has already been logged today.  Avoid
   *  using this for nested selectors (`&:hover` etc.) — top-level
   *  keys only; the consumer is responsible for not stomping on
   *  the row's built-in hover/zebra styling. */
  getRowSx?: (account: FollowUpAccount) => Record<string, unknown>;
  /** Optional renderer for an extra trailing block inside the
   *  ACTION cell of every row.  Used by the Today's Plan surface
   *  to fold the picks-reason / proximity / last-visit chips into
   *  the same "system says" column as the tier badge, so reps see
   *  one consolidated block instead of hunting across columns. */
  actionCellAppend?: (account: FollowUpAccount) => ReactNode;
  /** Optional override that REPLACES the entire ACTION cell content
   *  for every row (tier badge + explanation + appendNode are all
   *  skipped when set).  Used by the Today's Plan surface to render
   *  a plain-English "why now" narrative instead of the system's
   *  abstract REVIEW / RECOVERY / CROSS-SELL tier labels, which
   *  don't read well to a field rep who just wants to know what to
   *  do next. */
  actionCellOverride?: (account: FollowUpAccount, bucketLabel: string) => ReactNode;
  /** Override the ACTION column header label.  Defaults to "Action".
   *  Pair with `actionCellOverride` when the column is being
   *  repurposed (e.g. "Why now"). */
  actionColumnLabel?: string;
  /** Override the "How to read this" explainer rows at the bottom of
   *  the section.  Default rows describe the Depletions / Browse
   *  Accounts vocabulary (tier badges, momentum, sparkline);
   *  surfaces like Today's Plan that use different chrome should
   *  pass their own rows so the reader's guide stays accurate. */
  explainerRows?: SectionExplainerRow[];
  /** Override the state options shown in the "Filter by State" dropdown.
   *  When provided, the dropdown lists these states instead of deriving
   *  from the current bucket's accounts.  Today's Plan passes the rep's
   *  full territory_states so every accessible state is filterable even
   *  when the top-N picks are all in one state. */
  stateOptions?: string[];
  /** The rep's own states. Listed first, under "My territory"; anything
   *  else in `stateOptions` follows under "Other states". Omit to render
   *  one flat list, which is what the Depletions dashboard wants. */
  territoryStates?: string[];
  /** Callback fired whenever the state filter selection changes.
   *  Today's Plan uses this to switch its data source to the full
   *  follow-up dataset when the rep filters to a specific state. */
  onStateFilterChange?: (states: string[]) => void;
}

export function FollowUpTracker({
  useDataSource,
  buildExport,
  rowActions,
  rowActionsHeader,
  rowActionsDescription,
  defaultBucket,
  hideHeading,
  hideFilterChips,
  getRowSx,
  actionCellAppend,
  actionCellOverride,
  actionColumnLabel,
  explainerRows,
  stateOptions: stateOptionsProp,
  territoryStates,
  onStateFilterChange,
}: FollowUpTrackerProps = {}) {
  const useSource = useDataSource ?? useFollowUpTracker;
  const { data, isLoading, error } = useSource();
  const [activeBucket, setActiveBucket] = useState<string>(defaultBucket ?? '30-60 days');
  const [search, setSearch] = useState('');
  // Multi-select: empty array = "all states". Order in the array doesn't matter.
  const [stateFilter, setStateFilter] = useState<string[]>([]);
  const handleStateFilterChange = (states: string[]) => {
    setStateFilter(states);
    onStateFilterChange?.(states);
  };
  const [velocityFilter, setVelocityFilter] = useState<VelocityFilter>('all');
  // Action multi-select. Empty set = "all actions" (no filter).
  // Lets the rep ask "show me Mono-SKU accounts in this bucket".
  const [actionFilter, setActionFilter] = useState<Set<ActionFlag>>(new Set());
  // Premises multi-select. Empty set = "all premises types" (no filter).
  // Lets the rep narrow to e.g. on-premise (bars) or off-premise (retail).
  // 'UNKNOWN' represents accounts where premises_type is null in DB.
  const [premisesFilter, setPremisesFilter] = useState<Set<string>>(new Set());
  const [sortBy, setSortBy] = useState<SortBy>('rank');
  // Pagination — default 25 rows/page is the analytics-dashboard
  // convention (Stripe, Linear). User can switch to 50 / 100 / All.
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Whenever the active bucket OR any filter / sort changes, reset
  // to page 0 so the rep doesn't end up looking at page 5 of an
  // empty filtered list.
  useEffect(() => {
    setPage(0);
  }, [activeBucket, search, stateFilter, velocityFilter, actionFilter, premisesFilter, sortBy]);

  const orderedBuckets = useMemo<FollowUpBucket[]>(() => {
    if (!data) return [];
    // Standard buckets in canonical order, then anything else that
    // showed up in the data — that's the escape hatch the Field
    // Sales surface uses to pass in a synthetic "Today's Plan"
    // bucket without the BUCKET_ORDER list having to know about it.
    const known = BUCKET_ORDER.map((label) => data.buckets.find((b) => b.label === label)).filter(
      (b): b is FollowUpBucket => b !== undefined,
    );
    const knownLabels = new Set(known.map((b) => b.label));
    const extras = data.buckets.filter((b) => !knownLabels.has(b.label));
    return [...extras, ...known];
  }, [data]);

  const totalAccounts = orderedBuckets.reduce((s, b) => s + b.count, 0);

  const currentBucket = orderedBuckets.find((b) => b.label === activeBucket) ?? orderedBuckets[0];
  const currentColor = currentBucket
    ? (BUCKET_COLORS[currentBucket.label] ?? colors.chartPrimary)
    : colors.chartPrimary;
  const currentMeta = currentBucket ? BUCKET_META[currentBucket.label] : undefined;

  // Unique state codes for the "Filter by State" dropdown.  When the
  // caller provides an explicit list (e.g. Today's Plan passing all
  // territory_states), use that directly so every accessible state is
  // filterable even if the current picks only cover one state.
  const stateOptions = useMemo(() => {
    if (stateOptionsProp && stateOptionsProp.length > 0) return stateOptionsProp;
    if (!currentBucket) return [] as string[];
    return Array.from(
      new Set(currentBucket.accounts.map((a) => a.state_code).filter((s): s is string => !!s)),
    ).sort();
  }, [currentBucket, stateOptionsProp]);

  // Why is this empty? "No accounts in this bucket" is true but unhelpful
  // when a rep has just filtered to a state and got nothing back — they
  // cannot tell a quiet bucket from a broken filter.
  //
  // The tracker INNER JOINs depletions.facts, so a state whose accounts
  // have no shipment history yet returns nothing in EVERY bucket. That is
  // the case worth naming: the accounts are real, we simply have no
  // depletions for them.
  const accountsInScope = useMemo(
    () => (data?.buckets ?? []).reduce((n, b) => n + (b.accounts?.length ?? 0), 0),
    [data],
  );
  const filteredStateLabel = stateFilter.join(', ');
  const [emptyMessage, emptyHint] = useMemo((): [string, string | null] => {
    if (search) return ['No accounts match your search.', null];
    if (stateFilter.length > 0 && accountsInScope === 0) {
      return [
        `No shipments reported in ${filteredStateLabel} yet.`,
        'The accounts are there, but no depletions have come through for them — so there is nothing to rank. Use "Add an Account" if you need to log a visit here.',
      ];
    }
    if (stateFilter.length > 0) {
      return [`No accounts in this bucket for ${filteredStateLabel}.`, null];
    }
    return ['No accounts in this bucket.', null];
  }, [search, stateFilter.length, accountsInScope, filteredStateLabel]);

  // Account-tier map — ranks every account in the bucket by lifetime
  // 9L and tags the top 10% as TOP, next 15% as BIG, next 25% as MID.
  // Bottom 50% get no badge so the visual signal is reserved for
  // accounts that actually matter for prioritization.
  // Per-account action flags inside the active bucket. Recomputed
  // whenever the bucket changes; the reference_date stays constant
  // for the whole response so it's safe to derive here.
  const flagsByAccount = useMemo(() => {
    const map = new Map<number, AccountActionFlags>();
    if (!currentBucket) return map;
    const ref = data?.reference_date ?? null;
    for (const acc of currentBucket.accounts) {
      map.set(acc.account_id, deriveActionFlags(acc, ref));
    }
    return map;
  }, [currentBucket, data?.reference_date]);

  // Total counts per action across the active bucket — feeds the
  // small action-chip summary strip near the top of the table.
  const actionCounts = useMemo(() => {
    const counts: Record<ActionFlag, number> = {
      core_gap: 0,
      mono_sku: 0,
      fresh: 0,
      at_risk: 0,
      recovery: 0,
      defend: 0,
    };
    flagsByAccount.forEach((f) => {
      if (f.core_gap) counts.core_gap += 1;
      if (f.mono_sku) counts.mono_sku += 1;
      if (f.fresh) counts.fresh += 1;
      if (f.at_risk) counts.at_risk += 1;
      if (f.recovery) counts.recovery += 1;
      if (f.defend) counts.defend += 1;
    });
    return counts;
  }, [flagsByAccount]);

  const tierByAccount = useMemo(() => {
    const map = new Map<number, Tier | null>();
    if (!currentBucket) return map;
    const sorted = [...currentBucket.accounts].sort(
      (a, b) => Number(b.total_9l) - Number(a.total_9l),
    );
    const total = sorted.length;
    if (total === 0) return map;
    const top10 = Math.max(1, Math.ceil(total * 0.1));
    const top25 = Math.max(1, Math.ceil(total * 0.25));
    const top50 = Math.max(1, Math.ceil(total * 0.5));
    sorted.forEach((acc, i) => {
      if (i < top10) map.set(acc.account_id, 'TOP');
      else if (i < top25) map.set(acc.account_id, 'BIG');
      else if (i < top50) map.set(acc.account_id, 'MID');
      else map.set(acc.account_id, null);
    });
    return map;
  }, [currentBucket]);

  const filtered = useMemo(() => {
    if (!currentBucket) return [];
    const q = search.trim().toLowerCase();
    let rows = currentBucket.accounts.filter((a) => {
      if (q) {
        const matches =
          a.name.toLowerCase().includes(q) ||
          (a.city ?? '').toLowerCase().includes(q) ||
          (a.state_code ?? '').toLowerCase().includes(q) ||
          (a.distributor_name ?? '').toLowerCase().includes(q);
        if (!matches) return false;
      }
      if (stateFilter.length > 0 && !(a.state_code && stateFilter.includes(a.state_code)))
        return false;
      if (actionFilter.size > 0) {
        const f = flagsByAccount.get(a.account_id);
        if (!f) return false;
        // OR-match: account must hit at least one selected action.
        let any = false;
        for (const flag of actionFilter) {
          if (f[flag]) {
            any = true;
            break;
          }
        }
        if (!any) return false;
      }
      if (velocityFilter !== 'all') {
        const recent = Number(a.recent_3m_9l);
        const prior = Number(a.prior_3m_9l);
        const isNewActivity = prior === 0 && recent > 0;
        const pct = a.velocity_pct;
        switch (velocityFilter) {
          case 'growing':
            if (!(pct !== null && pct >= 10)) return false;
            break;
          case 'declining':
            if (!(pct !== null && pct <= -10)) return false;
            break;
          case 'steady':
            if (!(pct !== null && Math.abs(pct) < 10)) return false;
            break;
          case 'new':
            if (!isNewActivity) return false;
            break;
        }
      }
      if (premisesFilter.size > 0) {
        // Online retailers are their own channel (carved out of OFF);
        // otherwise treat null premises_type as the 'UNKNOWN' bucket.
        const key = a.is_online_retailer ? 'ONLINE' : (a.premises_type ?? 'UNKNOWN');
        if (!premisesFilter.has(key)) return false;
      }
      return true;
    });

    // Sort
    rows = [...rows];
    switch (sortBy) {
      case 'volume_desc':
        rows.sort((a, b) => Number(b.total_9l) - Number(a.total_9l));
        break;
      case 'velocity_desc':
        rows.sort((a, b) => (b.velocity_pct ?? -Infinity) - (a.velocity_pct ?? -Infinity));
        break;
      case 'velocity_asc':
        rows.sort((a, b) => (a.velocity_pct ?? Infinity) - (b.velocity_pct ?? Infinity));
        break;
      case 'recent_first':
        rows.sort((a, b) => a.days_since - b.days_since);
        break;
      case 'rank':
      default:
        // API returns sorted by days_since asc. Bump core-whiskey-gap
        // accounts (missing a Rye or Bourbon) to the top per the ranking
        // criteria, preserving the days_since order within each group —
        // a stable partition (Array.sort is stable in modern engines).
        rows.sort((a, b) => {
          const ag = flagsByAccount.get(a.account_id)?.core_gap ? 1 : 0;
          const bg = flagsByAccount.get(b.account_id)?.core_gap ? 1 : 0;
          return bg - ag;
        });
        break;
    }
    return rows;
  }, [
    currentBucket,
    search,
    stateFilter,
    velocityFilter,
    actionFilter,
    premisesFilter,
    flagsByAccount,
    sortBy,
  ]);

  const activeFilterCount =
    (search ? 1 : 0) +
    (stateFilter.length > 0 ? 1 : 0) +
    (velocityFilter !== 'all' ? 1 : 0) +
    (premisesFilter.size > 0 ? 1 : 0);

  const resetFilters = () => {
    setSearch('');
    handleStateFilterChange([]);
    setVelocityFilter('all');
    setActionFilter(new Set());
    setPremisesFilter(new Set());
    setSortBy('rank');
  };

  // Excel export: every row of `filtered` (all pages), in the on-screen order.
  useSectionExport((): ExcelWorkbookSpec | null => {
    const listOrAll = (xs: string[]): string => (xs.length > 0 ? xs.join(', ') : 'All');
    const filters: ExportFilter[] = [
      // The bucket tab is the primary filter here. Hosts that pin a single
      // built-in bucket (Today's Plan hides the chip row) describe their own scope.
      ...(currentBucket && !hideFilterChips
        ? [{ label: 'Days since last order (tab)', value: currentBucket.label }]
        : []),
      {
        label: 'Search (account, city, state or distributor)',
        value: search.trim() ? `"${search.trim()}"` : '',
      },
      {
        label: 'State',
        value: stateFilter.length > 0 ? [...stateFilter].sort().join(', ') : 'All states',
      },
      // The chip row is hidden on some hosts; those filters then don't exist.
      ...(hideFilterChips
        ? []
        : [
            {
              label: 'Action',
              value: listOrAll(
                ACTION_PRIORITY.filter((f) => actionFilter.has(f)).map(
                  (f) => ACTION_DETAIL[f].label,
                ),
              ),
            },
            { label: 'Momentum', value: VELOCITY_FILTER_LABEL[velocityFilter] },
            {
              label: 'Premises',
              value: listOrAll(
                ['ON', 'OFF', 'ONLINE', 'NA', 'UNKNOWN']
                  .filter((k) => premisesFilter.has(k))
                  .map((k) => PREMISES_STYLE[k]?.label ?? 'Unknown'),
              ),
            },
          ]),
      { kind: 'sort', label: 'Sorted by', value: SORT_EXPORT_LABEL[sortBy] },
      {
        kind: 'info',
        label: 'Data through',
        value: data?.data_window?.data_through ?? data?.reference_date ?? '',
      },
    ];
    if (buildExport) return buildExport(filtered, filters);
    if (!currentBucket) return null;
    const label = currentBucket.label;
    const rows = filtered.map((acc, i) => ({ ...acc, rank: i + 1 }));
    const flagLabels = (acc: FollowUpAccount): string[] => {
      const f = flagsByAccount.get(acc.account_id);
      return f ? ACTION_PRIORITY.filter((k) => f[k]).map((k) => ACTION_DETAIL[k].label) : [];
    };
    return {
      scope: label,
      title: 'Follow-Up Tracker',
      filters,
      sheets: [
        excelSheet({
          name: label,
          rows,
          columns: [
            { header: 'Rank', value: (r) => r.rank, format: 'int', width: 7 },
            ...accountColumns<(typeof rows)[number]>(),
            { header: 'Distributor', value: (r) => r.distributor_name, width: 26 },
            {
              header: 'Premises',
              value: (r) => premisesLabel(r.premises_type, r.is_online_retailer),
              width: 12,
            },
            { header: 'Tier', value: (r) => tierByAccount.get(r.account_id) ?? '', width: 7 },
            {
              header: 'Action',
              value: (r) =>
                flagLabels(r)[0] ??
                suggestedAction(
                  label,
                  r.velocity_pct,
                  Number(r.recent_3m_9l),
                  Number(r.prior_3m_9l),
                ).label,
              width: 18,
            },
            { header: 'Flags', value: (r) => flagLabels(r).join(', '), width: 24 },
            { header: 'Last active', value: (r) => isoDate(r.last_active), format: 'date' },
            { header: 'Days since', value: (r) => r.days_since, format: 'int' },
            { header: 'Total 9L', value: (r) => num(r.total_9l), format: 'decimal' },
            { header: 'Recent 3M 9L', value: (r) => num(r.recent_3m_9l), format: 'decimal' },
            { header: 'Prior 3M 9L', value: (r) => num(r.prior_3m_9l), format: 'decimal' },
            {
              header: 'Velocity %',
              value: (r) => (r.velocity_pct === null ? null : r.velocity_pct / 100),
              format: 'percent',
            },
            { header: 'Products carried', value: (r) => (r.products ?? []).join(', '), width: 30 },
            {
              header: 'Missing products',
              value: (r) => (r.missing_products ?? []).join(', '),
              width: 30,
            },
            { header: 'Customer since', value: (r) => isoDate(r.customer_since), format: 'date' },
          ],
        }),
      ],
    };
  }, !!data && !isLoading);

  if (error) {
    return (
      <Paper
        elevation={0}
        sx={{ p: 2, border: `1px solid ${colors.error}40`, bgcolor: `${colors.error}05` }}
      >
        <Typography color="error">Failed to load follow-up tracker: {error.message}</Typography>
      </Paper>
    );
  }

  if (isLoading || !data) {
    return (
      <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          Follow-Up Tracker
        </Typography>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 3, border: `1px solid ${colors.border}`, borderRadius: 2 }}>
      {/* Header — hidden on surfaces (Field Sales) where the
          page-level H1 + territory panel already orient the rep. */}
      {!hideHeading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${colors.chartPrimary}14`,
            }}
          >
            <NotificationsActiveIcon sx={{ color: colors.chartPrimary, fontSize: 20 }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Follow-Up Tracker
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Accounts grouped by days since their last non-zero depletion (
              {formatRef(data.reference_date)} reference). Click a segment to drill into who needs
              attention first.
            </Typography>
          </Box>
        </Box>
      )}

      {/* DESKTOP: segmented pipeline bar.
          Equal-width segments — no longer width-proportional to count
          because the AT RISK bucket (1,700+) dominates so heavily that
          the other buckets become unreadable. Each segment now gets
          the same horizontal real estate; the count + a subtle filled
          gauge bar inside conveys magnitude. Premium-slim profile.

          Hidden when there's only one bucket (e.g. the Field Sales
          surface's synthetic "Today's Plan" view) — a single-segment
          bar would just be visual noise. */}
      {orderedBuckets.length > 1 && (
        <Box sx={{ display: { xs: 'none', md: 'block' }, mb: 1 }}>
          <Box
            sx={{
              display: 'flex',
              gap: 0.75,
              height: 42,
            }}
          >
            {orderedBuckets.map((bucket) => {
              const color = BUCKET_COLORS[bucket.label] ?? colors.chartPrimary;
              const meta = BUCKET_META[bucket.label];
              const isActive = bucket.label === activeBucket;
              // Relative gauge inside each segment — visualises this
              // bucket's share without forcing the entire segment to be
              // narrow or wide.
              const sharePct = totalAccounts > 0 ? (bucket.count / totalAccounts) * 100 : 0;
              return (
                <Tooltip
                  key={bucket.label}
                  arrow
                  placement="bottom"
                  title={
                    meta && (
                      <Box>
                        <Typography sx={{ fontSize: 12, fontWeight: 700, color, mb: 0.25 }}>
                          {meta.action}
                        </Typography>
                        <Typography sx={{ fontSize: 11, color: colors.textMuted, mb: 0.75 }}>
                          {meta.timeLabel}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, mb: 0.75 }}>
                          {bucket.count} accounts · {fmt6PkNumber(bucket.total_9l)} 6-pk ·{' '}
                          {fmt.format(Number(bucket.total_9l))} 9L · {sharePct.toFixed(1)}% of
                          bucket base
                        </Typography>
                        <Typography
                          sx={{ fontSize: 11, color: colors.textSecondary, fontStyle: 'italic' }}
                        >
                          {meta.desc}
                        </Typography>
                      </Box>
                    )
                  }
                  slotProps={{
                    tooltip: {
                      sx: {
                        bgcolor: '#fff',
                        borderRadius: 1.5,
                        border: `1px solid ${colors.border}`,
                        px: 2,
                        py: 1.5,
                        boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                        maxWidth: 300,
                      },
                    },
                    arrow: {
                      sx: {
                        color: '#fff',
                        '&::before': { border: `1px solid ${colors.border}` },
                      },
                    },
                  }}
                >
                  <Box
                    onClick={() => setActiveBucket(bucket.label)}
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      position: 'relative',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      borderRadius: 1.25,
                      bgcolor: isActive ? color : `${color}10`,
                      border: `1px solid ${isActive ? color : `${color}33`}`,
                      boxShadow: isActive ? `0 2px 8px ${color}33` : 'none',
                      transition: 'all 0.15s ease',
                      overflow: 'hidden',
                      '&:hover': {
                        bgcolor: isActive ? color : `${color}1f`,
                        borderColor: isActive ? color : `${color}66`,
                        transform: 'translateY(-1px)',
                      },
                    }}
                  >
                    {/* Hairline gauge across the bottom edge — shows the
                      share of base for this bucket without taking
                      vertical space. */}
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: 2,
                        bgcolor: isActive ? 'rgba(255,255,255,0.35)' : `${color}1a`,
                      }}
                    >
                      <Box
                        sx={{
                          width: `${Math.max(2, sharePct)}%`,
                          height: '100%',
                          bgcolor: isActive ? 'rgba(255,255,255,0.85)' : color,
                          transition: 'width 0.3s ease',
                        }}
                      />
                    </Box>

                    <Typography
                      sx={{
                        fontSize: 14,
                        fontWeight: 700,
                        color: isActive ? '#fff' : colors.textPrimary,
                        lineHeight: 1,
                        fontFeatureSettings: '"tnum"',
                        letterSpacing: '-0.01em',
                      }}
                    >
                      {bucket.count}
                    </Typography>
                    {meta && (
                      <Typography
                        sx={{
                          fontSize: 8.5,
                          fontWeight: 700,
                          color: isActive ? 'rgba(255,255,255,0.9)' : color,
                          textTransform: 'uppercase',
                          letterSpacing: '0.1em',
                          mt: 0.25,
                        }}
                      >
                        {meta.action}
                      </Typography>
                    )}
                  </Box>
                </Tooltip>
              );
            })}
          </Box>

          {/* Compact time-range labels — abbreviated, center-aligned with
            each segment, same flex so they line up perfectly. */}
          <Box sx={{ display: 'flex', gap: 0.75, mt: 0.6 }}>
            {orderedBuckets.map((bucket) => {
              const isActive = bucket.label === activeBucket;
              const color = BUCKET_COLORS[bucket.label] ?? colors.chartPrimary;
              return (
                <Box
                  key={bucket.label}
                  onClick={() => setActiveBucket(bucket.label)}
                  sx={{
                    flex: 1,
                    textAlign: 'center',
                    cursor: 'pointer',
                    minWidth: 0,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 10,
                      color: isActive ? color : colors.textMuted,
                      fontWeight: isActive ? 600 : 500,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      letterSpacing: '0.01em',
                    }}
                  >
                    {SHORT_BUCKET_LABEL[bucket.label] ?? bucket.label}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* MOBILE: vertical list */}
      {orderedBuckets.length > 1 && (
        <Box
          sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 0.75, mb: 2 }}
        >
          {orderedBuckets.map((bucket) => {
            const meta = BUCKET_META[bucket.label];
            const isActive = bucket.label === activeBucket;
            const color = BUCKET_COLORS[bucket.label] ?? colors.chartPrimary;
            const pct = totalAccounts > 0 ? (bucket.count / totalAccounts) * 100 : 0;
            return (
              <Box
                key={bucket.label}
                onClick={() => setActiveBucket(bucket.label)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.25,
                  px: 1.5,
                  py: 1,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  border: `1.5px solid ${isActive ? color : colors.borderLight}`,
                  bgcolor: isActive ? `${color}0a` : '#fff',
                  transition: 'all 0.15s',
                }}
              >
                <Box
                  sx={{
                    width: 4,
                    alignSelf: 'stretch',
                    borderRadius: 2,
                    bgcolor: color,
                    opacity: isActive ? 1 : 0.35,
                  }}
                />
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: isActive ? color : colors.textSecondary,
                    }}
                  >
                    {meta?.action ?? bucket.label}
                  </Typography>
                  <Typography sx={{ fontSize: 10, color: colors.textMuted }}>
                    {meta?.timeLabel ?? ''}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1,
                    bgcolor: isActive ? `${color}1f` : colors.surfaceSubtle,
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: 13,
                      fontWeight: 800,
                      color: isActive ? color : colors.textPrimary,
                      fontFeatureSettings: '"tnum"',
                    }}
                  >
                    {bucket.count}
                  </Typography>
                </Box>
                <Typography
                  sx={{ fontSize: 10, color: colors.textMuted, width: 32, textAlign: 'right' }}
                >
                  {pct.toFixed(0)}%
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}

      {/* Insight callout for the selected bucket */}
      {currentBucket && currentMeta && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.25,
            pl: 1.25,
            pr: 2,
            py: 1.1,
            mt: 2,
            mb: 2,
            borderRadius: 1.5,
            bgcolor: `${currentColor}0d`,
            border: `1px solid ${currentColor}33`,
          }}
        >
          <Box
            sx={{
              width: 24,
              height: 24,
              borderRadius: 1.25,
              flexShrink: 0,
              bgcolor: `${currentColor}26`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <AccessTimeIcon sx={{ color: currentColor, fontSize: 14 }} />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.25, flexWrap: 'wrap' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: currentColor }}>
                {currentMeta.action}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: colors.textMuted }}>
                — {currentMeta.timeLabel.toLowerCase()}
              </Typography>
              <Chip
                label={`${currentBucket.count} accounts`}
                size="small"
                sx={{
                  fontSize: 10.5,
                  height: 20,
                  fontWeight: 600,
                  bgcolor: `${currentColor}1f`,
                  color: currentColor,
                }}
              />
            </Box>
            <Typography sx={{ fontSize: 12, color: colors.textSecondary }}>
              {currentMeta.desc}
            </Typography>
          </Box>
          {/* Two-stat group: 6-pack equivalent + 9L.  Mirrors the
              "6-pk · 9L" pattern reps see elsewhere in the table so
              the same volume can be read in whichever unit makes
              sense (distributor conversation vs. sales planning). */}
          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'stretch',
              gap: 2,
              flexShrink: 0,
            }}
          >
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {fmt6PkNumber(currentBucket.total_9l)}
              </Typography>
              <Typography sx={{ fontSize: 10, color: colors.textMuted, mt: 0.4 }}>
                6-pk total
              </Typography>
            </Box>
            <Box
              sx={{ width: '1px', bgcolor: `${currentColor}33`, alignSelf: 'stretch' }}
              aria-hidden
            />
            <Box sx={{ textAlign: 'center', display: 'flex', flexDirection: 'column' }}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.textPrimary,
                  lineHeight: 1,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                {fmt.format(Number(currentBucket.total_9l))}
              </Typography>
              <Typography sx={{ fontSize: 10, color: colors.textMuted, mt: 0.4 }}>
                9L total
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {/* Filter & sort toolbar */}
      {currentBucket && (
        <Box sx={{ mb: 1.75 }}>
          {/* Row 1: search + state + sort + reset */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1.25,
              flexWrap: 'wrap',
              mb: 1,
            }}
          >
            <TextField
              size="small"
              placeholder="Search account, city, state, distributor…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{
                flex: 1,
                minWidth: { xs: '100%', sm: 280 },
                '& .MuiInputBase-input': { fontSize: 13 },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} sx={{ p: 0.25 }}>
                      <ClearIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />

            {/* State filter. Empty selection = all states. */}
            <StateFilterSelect
              value={stateFilter}
              onChange={handleStateFilterChange}
              states={stateOptions}
              territoryStates={territoryStates}
              emptyLabel="All states"
              resetLabel="Clear"
            />

            {/* Sort */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortBy)}
                renderValue={(v) => (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <SortIcon sx={{ fontSize: 16, color: colors.textMuted }} />
                    <Typography sx={{ fontSize: 13 }}>{SORT_LABEL[v as SortBy]}</Typography>
                  </Box>
                )}
                sx={{ '& .MuiSelect-select': { py: 0.85 } }}
              >
                {(Object.keys(SORT_LABEL) as SortBy[]).map((k) => (
                  <MenuItem key={k} value={k}>
                    {SORT_LABEL[k]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {activeFilterCount > 0 && (
              <Tooltip title="Clear all filters" placement="top" arrow>
                <IconButton
                  size="small"
                  onClick={resetFilters}
                  sx={{
                    border: `1px solid ${colors.borderLight}`,
                    borderRadius: 1,
                    px: 1,
                    fontSize: 11,
                    color: colors.textSecondary,
                    '&:hover': { bgcolor: colors.surfaceSubtle },
                  }}
                >
                  <ClearIcon sx={{ fontSize: 14, mr: 0.5 }} />
                  <Typography sx={{ fontSize: 11, fontWeight: 600 }}>
                    Clear ({activeFilterCount})
                  </Typography>
                </IconButton>
              </Tooltip>
            )}
          </Box>

          {/* Row 2: velocity chip filters + result count.
              Suppressed on curated surfaces (Today's Plan) where
              re-filtering would defeat the system's picks. */}
          {!hideFilterChips && (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              {/* Action chips — strategic signals derived from the
                row data. Lets the rep filter by call type (at-risk,
                recovery, cross-sell, nurture) on top of the bucket.
                Heatmap's Recommendations panel folds into here. */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mr: 0.5,
                  }}
                >
                  Action:
                </Typography>
                {ACTION_ORDER.map((flag) => {
                  const active = actionFilter.has(flag);
                  const count = actionCounts[flag];
                  const style = ACTION_STYLE[flag];
                  return (
                    <Tooltip
                      key={flag}
                      placement="top"
                      arrow
                      title={
                        <Box sx={{ p: 0.25, maxWidth: 260 }}>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 700,
                              color: style.color,
                              mb: 0.4,
                            }}
                          >
                            {ACTION_LABEL[flag]}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: colors.textSecondary, mb: 0.6 }}>
                            {ACTION_DESCRIBE[flag]}
                          </Typography>
                          <Box
                            sx={{
                              borderTop: `1px solid ${colors.borderLight}`,
                              pt: 0.5,
                              mb: 0.4,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                color: colors.textMuted,
                              }}
                            >
                              This pill (filter)
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: colors.textSecondary }}>
                              Narrows the table to the {count}{' '}
                              {count === 1 ? 'account' : 'accounts'} in this bucket flagged{' '}
                              {ACTION_LABEL[flag]}.
                            </Typography>
                          </Box>
                          <Box>
                            <Typography
                              sx={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                letterSpacing: '0.05em',
                                textTransform: 'uppercase',
                                color: colors.textMuted,
                              }}
                            >
                              Action column (verdict)
                            </Typography>
                            <Typography sx={{ fontSize: 10.5, color: colors.textSecondary }}>
                              Shows the per-row recommendation with full evidence on hover (rule,
                              data points, suggested step).
                            </Typography>
                          </Box>
                          <Typography
                            sx={{
                              fontSize: 10.5,
                              color: colors.textMuted,
                              mt: 0.6,
                              fontStyle: 'italic',
                            }}
                          >
                            Click to {active ? 'remove' : 'filter to'} this action.
                          </Typography>
                        </Box>
                      }
                      slotProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#fff',
                            borderRadius: 1.5,
                            border: `1px solid ${colors.border}`,
                            px: 1.5,
                            py: 1,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                            maxWidth: 260,
                          },
                        },
                        arrow: {
                          sx: {
                            color: '#fff',
                            '&::before': { border: `1px solid ${colors.border}` },
                          },
                        },
                      }}
                    >
                      <Chip
                        label={
                          <Box
                            component="span"
                            sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.4 }}
                          >
                            <span>{ACTION_LABEL[flag]}</span>
                            <Box
                              component="span"
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: active ? style.color : colors.textMuted,
                                fontFeatureSettings: '"tnum"',
                              }}
                            >
                              {count}
                            </Box>
                          </Box>
                        }
                        size="small"
                        onClick={() => {
                          const next = new Set(actionFilter);
                          if (next.has(flag)) next.delete(flag);
                          else next.add(flag);
                          setActionFilter(next);
                        }}
                        disabled={count === 0}
                        sx={{
                          height: 24,
                          fontSize: 11.5,
                          fontWeight: 600,
                          cursor: count === 0 ? 'default' : 'pointer',
                          bgcolor: active ? style.bg : 'transparent',
                          color: active ? style.color : colors.textSecondary,
                          border: `1px solid ${active ? `${style.color}55` : colors.borderLight}`,
                          opacity: count === 0 ? 0.45 : 1,
                          transition: 'all 0.12s',
                          '&:hover':
                            count === 0
                              ? {}
                              : {
                                  bgcolor: active ? `${style.color}26` : colors.surfaceSubtle,
                                  borderColor: active ? style.color : colors.border,
                                },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mr: 0.5,
                  }}
                >
                  Momentum:
                </Typography>
                {(Object.keys(VELOCITY_FILTER_LABEL) as VelocityFilter[]).map((v) => {
                  const active = velocityFilter === v;
                  const accent =
                    v === 'growing'
                      ? colors.success
                      : v === 'declining'
                        ? colors.error
                        : v === 'new'
                          ? colors.chartPrimary
                          : v === 'steady'
                            ? colors.textMuted
                            : colors.textPrimary;
                  return (
                    <Chip
                      key={v}
                      label={VELOCITY_FILTER_LABEL[v]}
                      size="small"
                      onClick={() => setVelocityFilter(v)}
                      sx={{
                        height: 24,
                        fontSize: 11.5,
                        fontWeight: 600,
                        cursor: 'pointer',
                        bgcolor: active ? `${accent}1f` : 'transparent',
                        color: active ? accent : colors.textSecondary,
                        border: `1px solid ${active ? `${accent}55` : colors.borderLight}`,
                        transition: 'all 0.12s',
                        '&:hover': {
                          bgcolor: active ? `${accent}26` : colors.surfaceSubtle,
                          borderColor: active ? accent : colors.border,
                        },
                      }}
                    />
                  );
                })}
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography
                  sx={{
                    fontSize: 10.5,
                    fontWeight: 700,
                    color: colors.textMuted,
                    textTransform: 'uppercase',
                    letterSpacing: '0.08em',
                    mr: 0.5,
                  }}
                >
                  Premises:
                </Typography>
                {(['ON', 'OFF', 'ONLINE', 'NA', 'UNKNOWN'] as const).map((key) => {
                  const active = premisesFilter.has(key);
                  const count = currentBucket.accounts.filter(
                    (a) =>
                      (a.is_online_retailer ? 'ONLINE' : (a.premises_type ?? 'UNKNOWN')) === key,
                  ).length;
                  const style = PREMISES_STYLE[key] ?? {
                    label: 'Unknown',
                    color: colors.textMuted,
                    bg: colors.surfaceSubtle,
                    border: colors.borderLight,
                    description:
                      'Older accounts that predate the broker carrying premises classification.',
                  };
                  return (
                    <Tooltip
                      key={key}
                      placement="top"
                      arrow
                      title={
                        <Box sx={{ p: 0.25, maxWidth: 260 }}>
                          <Typography
                            sx={{
                              fontSize: 11.5,
                              fontWeight: 700,
                              color: style.color,
                              mb: 0.4,
                            }}
                          >
                            {style.label}
                          </Typography>
                          <Typography sx={{ fontSize: 11, color: colors.textSecondary, mb: 0.6 }}>
                            {style.description}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 10.5, color: colors.textMuted, fontStyle: 'italic' }}
                          >
                            Click to {active ? 'remove' : 'filter to'} this premises type.
                          </Typography>
                        </Box>
                      }
                      slotProps={{
                        tooltip: {
                          sx: {
                            bgcolor: '#fff',
                            borderRadius: 1.5,
                            border: `1px solid ${colors.border}`,
                            px: 1.5,
                            py: 1,
                            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                            maxWidth: 280,
                          },
                        },
                        arrow: {
                          sx: {
                            color: '#fff',
                            '&::before': { border: `1px solid ${colors.border}` },
                          },
                        },
                      }}
                    >
                      <Chip
                        label={
                          <Box
                            component="span"
                            sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.4 }}
                          >
                            <span>
                              {key === 'UNKNOWN' ? 'Unknown' : key === 'ONLINE' ? 'Online' : key}
                            </span>
                            <Box
                              component="span"
                              sx={{
                                fontSize: 10,
                                fontWeight: 700,
                                color: active ? style.color : colors.textMuted,
                                fontFeatureSettings: '"tnum"',
                              }}
                            >
                              {count}
                            </Box>
                          </Box>
                        }
                        size="small"
                        onClick={() => {
                          const next = new Set(premisesFilter);
                          if (next.has(key)) next.delete(key);
                          else next.add(key);
                          setPremisesFilter(next);
                        }}
                        disabled={count === 0}
                        sx={{
                          height: 24,
                          fontSize: 11.5,
                          fontWeight: 600,
                          cursor: count === 0 ? 'default' : 'pointer',
                          bgcolor: active ? style.bg : 'transparent',
                          color: active ? style.color : colors.textSecondary,
                          border: `1px solid ${active ? style.border : colors.borderLight}`,
                          opacity: count === 0 ? 0.45 : 1,
                          transition: 'all 0.12s',
                          '&:hover':
                            count === 0
                              ? {}
                              : {
                                  bgcolor: active ? `${style.color}26` : colors.surfaceSubtle,
                                  borderColor: active ? style.color : colors.border,
                                },
                        }}
                      />
                    </Tooltip>
                  );
                })}
              </Box>

              <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                {filtered.length} {filtered.length === 1 ? 'account' : 'accounts'} in view
                {activeFilterCount > 0 && (
                  <Box
                    component="span"
                    sx={{ color: colors.textMuted, fontStyle: 'italic', ml: 0.5 }}
                  >
                    · filtered from {currentBucket.accounts.length}
                  </Box>
                )}
              </Typography>
            </Box>
          )}

          {/* Insights philosophy card — compact editorial treatment in
              HY brand cream + gold. Two rows: tagline + three numbered
              principles in a single horizontal row.

              Hidden when there's only a single bucket (the synthetic
              "Today's Plan" view on Field Sales) — its messaging
              ("Built for your judgment, not in place of it") doesn't
              fit a system-curated picks list, and the callout repeats
              context the heading already establishes. */}
          {orderedBuckets.length > 1 && (
            <Box
              sx={{
                mt: 1.25,
                position: 'relative',
                overflow: 'hidden',
                borderRadius: 1.5,
                bgcolor: colors.brandCream,
                border: `1px solid ${colors.gold}40`,
                boxShadow: '0 1px 0 rgba(212,168,75,0.08)',
                pl: 2,
                pr: 1.75,
                py: 0.9,
                display: 'flex',
                // Stack vertically on phones; inline tagline + principles
                // on tablet and up so the bar stays one-row wherever it
                // can fit.
                flexDirection: { xs: 'column', md: 'row' },
                alignItems: { xs: 'flex-start', md: 'center' },
                flexWrap: 'wrap',
                columnGap: 2.5,
                rowGap: 0.75,
              }}
            >
              {/* Gold accent rule on the left edge */}
              <Box
                sx={{
                  position: 'absolute',
                  left: 0,
                  top: 0,
                  bottom: 0,
                  width: 2.5,
                  bgcolor: colors.gold,
                }}
              />

              {/* Tagline — wraps naturally if it can't fit one line. */}
              <Typography
                sx={{
                  fontSize: 12,
                  fontWeight: 700,
                  color: colors.brandDark,
                  letterSpacing: '-0.005em',
                  lineHeight: 1.4,
                }}
              >
                Built for your judgment,{' '}
                <Box component="span" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
                  not in place of it.
                </Box>
              </Typography>

              {/* Three principles — inline on wide screens, stacked on narrow. */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flex: 1,
                  minWidth: 0,
                  gap: { xs: 0.5, sm: 2 },
                  flexWrap: 'wrap',
                  width: { xs: '100%', md: 'auto' },
                }}
              >
                {[
                  { num: '01', eyebrow: 'WE SURFACE', body: 'patterns in your depletions data' },
                  { num: '02', eyebrow: 'YOU CHOOSE', body: 'whose account, whose moment' },
                  {
                    num: '03',
                    eyebrow: 'YOU CLOSE',
                    body: 'the conversation that earns the order',
                  },
                ].map((p) => (
                  <Box
                    key={p.num}
                    sx={{
                      display: 'flex',
                      alignItems: 'baseline',
                      gap: 0.6,
                      minWidth: 0,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: 11,
                        fontWeight: 400,
                        color: colors.gold,
                        fontFeatureSettings: '"tnum"',
                        letterSpacing: '0.02em',
                      }}
                    >
                      {p.num}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 9.5,
                        fontWeight: 800,
                        letterSpacing: '0.12em',
                        color: colors.brandDark,
                      }}
                    >
                      {p.eyebrow}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 11.5,
                        color: colors.textSecondary,
                        lineHeight: 1.4,
                      }}
                    >
                      {p.body}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* Account table */}
      {currentBucket && (
        <Box>
          {filtered.length === 0 ? (
            <Box sx={{ py: 2 }}>
              <Typography sx={{ fontSize: 13, color: colors.textMuted }}>{emptyMessage}</Typography>
              {emptyHint && (
                <Typography sx={{ fontSize: 12.5, color: colors.textMuted, mt: 0.5 }}>
                  {emptyHint}
                </Typography>
              )}
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: 460,
                borderRadius: 1.5,
                border: `1px solid ${colors.border}`,
                // Premium brand scrollbar — replaces the default grey
                // browser bar with a soft gold-tinted thumb that fits
                // the warm editorial palette.  Track is invisible
                // (transparent) so the gold thumb floats over the
                // table chrome.  Firefox gets the matching pair via
                // scrollbar-color; WebKit (Safari/Chrome/Edge) gets
                // the full custom treatment with a hover state.
                scrollbarColor: 'rgba(187, 140, 63, 0.42) transparent',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': {
                  width: 9,
                  height: 9,
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'rgba(187, 140, 63, 0.38)',
                  borderRadius: 8,
                  border: '2px solid transparent',
                  backgroundClip: 'content-box',
                  transition: 'background 0.2s ease',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  background: 'rgba(187, 140, 63, 0.65)',
                  backgroundClip: 'content-box',
                },
                '&::-webkit-scrollbar-corner': {
                  background: 'transparent',
                },
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      '& th': {
                        bgcolor: '#f8f9fb',
                        borderBottom: `2px solid ${colors.border}`,
                        py: 0.85,
                        px: 1.25,
                        fontSize: 10,
                        fontWeight: 700,
                        color: '#9ca3af',
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                      },
                    }}
                  >
                    <ColumnHeader
                      label="#"
                      width={36}
                      title="Rank"
                      desc="Position within this bucket, ordered by days since last order (most recent first). The top three ranks are highlighted in the bucket's color — those are the most urgent calls in this category."
                    />
                    <ColumnHeader
                      label="Account"
                      tooltipMaxWidth={380}
                      title="Retail account + value tier"
                      desc={
                        <>
                          The retail account name reported by iDIG — the physical store, bar,
                          restaurant, or military/duty-free venue that bought the product. NOT the
                          distributor that services them.
                          <br />
                          <br />
                          <strong>Tier badge</strong> — the chip next to the name ranks the account
                          by lifetime 6-pk <em>within this bucket</em>:
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              gap: 0.35,
                              mt: 0.5,
                              pl: 1,
                            }}
                          >
                            <Box
                              component="span"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 800,
                                  letterSpacing: '0.08em',
                                  px: 0.6,
                                  py: 0.15,
                                  borderRadius: 0.75,
                                  color: TIER_STYLE.TOP.color,
                                  bgcolor: TIER_STYLE.TOP.bg,
                                  border: `1px solid ${TIER_STYLE.TOP.border}`,
                                }}
                              >
                                TOP
                              </Box>
                              <Box component="span" sx={{ fontSize: 11.5 }}>
                                Top 10% by lifetime 6-pk — your highest-leverage calls.
                              </Box>
                            </Box>
                            <Box
                              component="span"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 800,
                                  letterSpacing: '0.08em',
                                  px: 0.6,
                                  py: 0.15,
                                  borderRadius: 0.75,
                                  color: TIER_STYLE.BIG.color,
                                  bgcolor: TIER_STYLE.BIG.bg,
                                  border: `1px solid ${TIER_STYLE.BIG.border}`,
                                }}
                              >
                                BIG
                              </Box>
                              <Box component="span" sx={{ fontSize: 11.5 }}>
                                Top 25% — meaningful accounts worth real attention.
                              </Box>
                            </Box>
                            <Box
                              component="span"
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                            >
                              <Box
                                component="span"
                                sx={{
                                  fontSize: 9,
                                  fontWeight: 800,
                                  letterSpacing: '0.08em',
                                  px: 0.6,
                                  py: 0.15,
                                  borderRadius: 0.75,
                                  color: TIER_STYLE.MID.color,
                                  bgcolor: TIER_STYLE.MID.bg,
                                  border: `1px solid ${TIER_STYLE.MID.border}`,
                                }}
                              >
                                MID
                              </Box>
                              <Box component="span" sx={{ fontSize: 11.5 }}>
                                Top 50% — steady contributors.
                              </Box>
                            </Box>
                            <Box
                              component="span"
                              sx={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}
                            >
                              Bottom 50% — no badge, so the chip stays a meaningful signal.
                            </Box>
                          </Box>
                        </>
                      }
                    />
                    <ColumnHeader
                      label="Location"
                      title="Where & how long"
                      desc={
                        <>
                          City &amp; state of the physical account, plus the iDIG distributor short
                          code (e.g.&nbsp;
                          <strong>FL13</strong>) that services it.
                          <br />
                          <br />
                          <strong>“since &lt;month&gt; · N mo”</strong> shows two things:
                          <Box component="span" sx={{ display: 'block', pl: 1, mt: 0.5 }}>
                            • <strong>since &lt;month&gt;</strong> — the first month this account
                            ever recorded a non-zero depletion.
                            <br />• <strong>N mo</strong> — relationship age, in whole months from
                            that first depletion to today.
                          </Box>
                          <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
                            A 24-month customer going cold is a much bigger problem than a 2-month
                            one — having the age inline lets the rep register that instantly.
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              mt: 1,
                              pt: 0.75,
                              borderTop: `1px dashed ${colors.borderLight}`,
                              fontSize: 11,
                              color: colors.textMuted,
                              fontStyle: 'italic',
                            }}
                          >
                            Hover any row&apos;s city/state to reveal the full mailing address
                            (street, ZIP, county) — useful for confirming the right venue before a
                            call or handing the address off to a territory tool.
                          </Box>
                        </>
                      }
                    />
                    <ColumnHeader
                      label="Products"
                      width={132}
                      title="SKUs ever purchased"
                      desc={
                        <>
                          Hooten &amp; Young SKUs this account has purchased at least once across
                          the full data window, sorted by total 6-pk moved per product (highest
                          first).
                          <br />
                          <br />
                          Up to two chips are shown; <strong>+N</strong> indicates additional SKUs.
                          Brand prefix (&ldquo;Hooten Young&rdquo;) is stripped so the chips fit
                          cleanly.
                        </>
                      }
                    />
                    <ColumnHeader
                      label="Missing Products"
                      width={132}
                      title="SKUs this account has never bought"
                      desc={
                        <>
                          The Hooten &amp; Young catalog SKUs this account has{' '}
                          <strong>never purchased</strong> — the gaps a rep can pitch to expand the
                          shelf.
                          <br />
                          <br />
                          Ordered by <strong>catalog-wide volume</strong> (best-selling gaps first),
                          so the strongest cross-sell candidates lead. Up to two chips are shown;{' '}
                          <strong>+N</strong> reveals the rest. An account carrying every SKU shows{' '}
                          <strong>Full range</strong>.
                        </>
                      }
                    />
                    <ColumnHeader
                      label="12-Month Trend"
                      width={160}
                      title="Monthly volume, last 12 months"
                      desc={
                        <>
                          One bar per month, ending at the reference date. Bar height is scaled
                          relative to <strong>this account&apos;s</strong> own peak month — the
                          solid-color bar — so the strip reads as a within-account pattern.
                          <br />
                          <br />
                          Use it to spot: <em>growing</em> (taller bars on the right),{' '}
                          <em>declining</em> (taller bars on the left), <em>seasonal</em> (clustered
                          peaks), or <em>sporadic</em> (random spikes). Zero-volume months sit on a
                          faint baseline rail so they&apos;re still visible.
                          <br />
                          <br />
                          <strong>“Peak &lt;month&gt; · N”</strong> caption — names the solid-color
                          bar (best month + 6-pk value) so it doubles as a conversation starter:{' '}
                          <em>
                            “you did N cases that month — what would it take to get back there?”
                          </em>
                        </>
                      }
                    />
                    <ColumnHeader
                      label="6-pk Vol"
                      align="right"
                      width={96}
                      title="Lifetime 6-pk + recent cadence"
                      desc={
                        <>
                          <strong>Top value</strong> — total 6-pack equivalents moved across the
                          <strong> full</strong> history we hold (one 9-liter equivalent case = 2
                          six-packs). Below it, the same lifetime total in 9L — the
                          industry-standard unit (one 9-liter equivalent case = 12&nbsp;×&nbsp;750ml
                          bottles), which rolls up cleanly across SKUs.
                          <br />
                          <br />
                          <strong>Caption below</strong> — average 6-pk per active month during the
                          last 12 months. Tells you cadence vs depth:
                          <Box component="span" sx={{ display: 'block', pl: 1, mt: 0.5 }}>
                            • A <em>2/mo</em> account is a small but regular buyer.
                            <br />• A <em>20/mo</em> account is a burst buyer that orders big and
                            irregularly.
                          </Box>
                        </>
                      }
                    />
                    <ColumnHeader
                      label="Last Order"
                      width={140}
                      tooltipMaxWidth={420}
                      title="Most recent activity + momentum"
                      desc={
                        <>
                          <strong>Month label</strong> — the account&apos;s most recent
                          <strong> non-zero</strong> depletion. Zero-volume rows are stored but
                          don&apos;t count as activity.
                          <Box
                            component="span"
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mt: 0.85,
                              flexWrap: 'wrap',
                            }}
                          >
                            <Box
                              component="span"
                              sx={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                color: colors.chartPrimary,
                                bgcolor: `${colors.chartPrimary}14`,
                                px: 0.6,
                                py: 0.1,
                                borderRadius: 0.75,
                                letterSpacing: '0.02em',
                              }}
                            >
                              47d
                            </Box>
                            <Box component="span" sx={{ fontSize: 11.5 }}>
                              <strong>Days-since pill</strong> — exact days between today and the
                              last order. Gives precision <em>within</em> a bucket so you can sort
                              the 30-day window from the 60-day edge.
                            </Box>
                          </Box>
                          <br />
                          The <strong>velocity badge below</strong> shows momentum — the last 3
                          months&apos; 6-pk total compared to the 3 months before that:
                          <Box component="span" sx={{ display: 'block', mt: 0.75 }}>
                            <strong style={{ color: colors.success }}>▲ growth</strong> ≥&nbsp;10% —
                            recent 3 months are higher than the prior 3.
                          </Box>
                          <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                            <strong style={{ color: colors.error }}>▼ decline</strong> ≥&nbsp;10% —
                            recent 3 months are lower than the prior 3.
                          </Box>
                          <Box component="span" sx={{ display: 'block', mt: 0.25 }}>
                            <strong style={{ color: colors.textMuted }}>≈ STEADY</strong> — change
                            within ±10%. Holding pattern.
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              mt: 1.25,
                              p: 1,
                              borderLeft: `2px solid ${colors.chartPrimary}`,
                              bgcolor: `${colors.chartPrimary}0a`,
                              borderRadius: '0 4px 4px 0',
                            }}
                          >
                            <strong style={{ color: colors.chartPrimary }}>NEW ACTIVITY</strong> —
                            the account had <strong>zero depletions</strong> in the prior 3-month
                            window, but is moving product in the last 3. With no prior baseline
                            there&apos;s no growth&nbsp;% to show, so the chip flags the situation
                            directly.
                            <Box component="span" sx={{ display: 'block', mt: 0.5 }}>
                              It means one of two things, both worth a call:
                            </Box>
                            <Box
                              component="span"
                              sx={{ display: 'block', pl: 1.5, mt: 0.25, lineHeight: 1.6 }}
                            >
                              • A <strong>brand-new account</strong> placing its first orders —
                              nurture the relationship while it&apos;s fresh.
                              <br />• A <strong>dormant account</strong> that came back to life — a
                              re-engagement win worth understanding (and protecting).
                            </Box>
                          </Box>
                        </>
                      }
                    />
                    <ColumnHeader
                      label={actionColumnLabel ?? 'Action'}
                      width={130}
                      tooltipMaxWidth={440}
                      title="Suggested next step"
                      desc={
                        <>
                          An opinionated next action for each account, computed from its
                          <strong> bucket × momentum × prior-baseline</strong>. Designed to make the
                          table prescriptive — scan the column for the action that fits your time
                          today (e.g. all <em>Reorder upsell</em> chips for closing calls; all
                          <em> Risk escalation</em> chips for the rescue list).
                          <br />
                          <br />
                          <strong>Key</strong>
                          <Box
                            component="span"
                            sx={{ display: 'block', mt: 0.5, lineHeight: 1.65 }}
                          >
                            <ActionKeyRow color={colors.success} label="Reorder upsell">
                              recent orderer trending up — push the next case or SKU.
                            </ActionKeyRow>
                            <ActionKeyRow color={colors.info} label="Thank-you">
                              recent orderer holding pattern — maintenance touch.
                            </ActionKeyRow>
                            <ActionKeyRow color={colors.success} label="Confirm supply">
                              trending up close to reorder — keep inventory ready.
                            </ActionKeyRow>
                            <ActionKeyRow color={colors.info} label="Check in">
                              1–2 months silent — light availability nudge.
                            </ActionKeyRow>
                            <ActionKeyRow color={colors.warning} label="Catalog refresh">
                              fading — share new releases / seasonal promos.
                            </ActionKeyRow>
                            <ActionKeyRow color={colors.chartPrimary} label="Re-engage call">
                              2–3 months silent — schedule a real conversation.
                            </ActionKeyRow>
                            <ActionKeyRow color="#f97316" label="Win-back">
                              3–4 months silent — incentives, case deals, diagnose the stall.
                            </ActionKeyRow>
                            <ActionKeyRow color={colors.chartPrimary} label="Welcome back">
                              dormant account that just resumed ordering — nurture the
                              re-engagement.
                            </ActionKeyRow>
                            <ActionKeyRow color={colors.error} label="Risk escalation">
                              4+ months silent — urgent rescue before the relationship dies.
                            </ActionKeyRow>
                          </Box>
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              mt: 1,
                              fontSize: 11,
                              color: colors.textMuted,
                              fontStyle: 'italic',
                            }}
                          >
                            Hover any chip in the column to see the specific reasoning the system
                            applied to that account.
                          </Box>
                        </>
                      }
                    />
                    {rowActions && (
                      <ColumnHeader
                        label={rowActionsHeader ?? 'Quick actions'}
                        width={170}
                        stickyRight
                        tooltipMaxWidth={320}
                        title="Per-account rep actions"
                        desc={
                          rowActionsDescription ?? (
                            <>
                              Pin / unpin the account, log a quick visit note, and see your
                              most-recent contact at a glance — no need to leave this view.
                            </>
                          )
                        }
                      />
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(rowsPerPage === -1
                    ? filtered
                    : filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  ).map((acc, idx) => {
                    // Continue ranking across pages so page 2 starts at "26",
                    // not "1" — the rank should reflect position in the
                    // overall sorted list, not the visible page.
                    const i = rowsPerPage === -1 ? idx : page * rowsPerPage + idx;
                    const topThree = i < 3;
                    const products = acc.products ?? [];
                    const missingProducts = acc.missing_products ?? [];
                    return (
                      <TableRow
                        key={acc.account_id}
                        sx={{
                          bgcolor: i % 2 === 0 ? '#fff' : '#fafbfc',
                          transition: 'background-color 0.15s, box-shadow 0.15s, opacity 0.2s',
                          position: 'relative',
                          '&:hover': {
                            bgcolor: `${currentColor}0a`,
                            // Subtle left-edge color highlight on hover —
                            // signals "this row is the focus" without being loud.
                            boxShadow: `inset 3px 0 0 0 ${currentColor}55`,
                            opacity: 1,
                            // Layer the translucent hover tint over the
                            // pinned cell's solid fill.
                            '& td.row-actions': {
                              backgroundImage: `linear-gradient(${currentColor}0a, ${currentColor}0a)`,
                            },
                          },
                          '& td': {
                            borderBottom: `1px solid ${colors.borderLight}`,
                            py: 1,
                            px: 1.25,
                            verticalAlign: 'middle',
                          },
                          ...(getRowSx?.(acc) ?? {}),
                        }}
                      >
                        <TableCell>
                          <Typography
                            sx={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: topThree ? currentColor : '#bfc4cb',
                            }}
                          >
                            {i + 1}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.75,
                              minWidth: 0,
                            }}
                          >
                            <Tooltip
                              title={acc.name}
                              placement="top-start"
                              arrow
                              enterDelay={500}
                              slotProps={{
                                tooltip: {
                                  sx: {
                                    bgcolor: '#fff',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: 1,
                                    px: 1.25,
                                    py: 0.5,
                                    color: colors.textPrimary,
                                    fontSize: 12,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                                  },
                                },
                                arrow: {
                                  sx: {
                                    color: '#fff',
                                    '&::before': { border: `1px solid ${colors.border}` },
                                  },
                                },
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: colors.textPrimary,
                                  whiteSpace: 'nowrap',
                                  maxWidth: 220,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  letterSpacing: '-0.005em',
                                }}
                              >
                                {acc.name}
                              </Typography>
                            </Tooltip>
                            <TierBadge tier={tierByAccount.get(acc.account_id) ?? null} />
                            <PremisesBadge
                              premises={acc.premises_type}
                              online={acc.is_online_retailer}
                            />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <LocationCell account={acc} referenceDate={data?.reference_date ?? ''} />
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.4,
                              rowGap: 0.4,
                              flexWrap: 'wrap',
                              maxWidth: 132,
                            }}
                          >
                            {products.slice(0, 2).map((p) => (
                              <Chip
                                key={p}
                                label={p}
                                size="small"
                                sx={{
                                  fontSize: 9.5,
                                  height: 18,
                                  fontWeight: 500,
                                  bgcolor: `${colors.chartPrimary}0d`,
                                  color: colors.chartPrimary,
                                  border: `1px solid ${colors.chartPrimary}26`,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            ))}
                            {products.length > 2 && (
                              <Tooltip
                                placement="top"
                                arrow
                                title={
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: colors.textPrimary,
                                        mb: 0.5,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      All products ({products.length})
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.35,
                                      }}
                                    >
                                      {products.map((p, idx) => (
                                        <Box
                                          key={p}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            fontSize: 11.5,
                                            color: colors.textSecondary,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 14,
                                              fontSize: 9.5,
                                              fontWeight: 700,
                                              color: colors.textMuted,
                                              textAlign: 'right',
                                            }}
                                          >
                                            {idx + 1}.
                                          </Box>
                                          {p}
                                        </Box>
                                      ))}
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontSize: 10,
                                        color: colors.textMuted,
                                        mt: 0.75,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      Sorted by total 6-pk moved, highest first.
                                    </Typography>
                                  </Box>
                                }
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: '#fff',
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: 1.5,
                                      px: 1.75,
                                      py: 1.25,
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                                      maxWidth: 280,
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: '#fff',
                                      '&::before': { border: `1px solid ${colors.border}` },
                                    },
                                  },
                                }}
                              >
                                <Chip
                                  label={`+${products.length - 2}`}
                                  size="small"
                                  sx={{
                                    fontSize: 9.5,
                                    height: 18,
                                    bgcolor: '#f3f4f6',
                                    color: '#6b7280',
                                    cursor: 'help',
                                    '& .MuiChip-label': { px: 0.75 },
                                    '&:hover': { bgcolor: '#e5e7eb' },
                                  }}
                                />
                              </Tooltip>
                            )}
                            {products.length === 0 && (
                              <Typography sx={{ fontSize: 11, color: colors.textMuted }}>
                                —
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              gap: 0.4,
                              rowGap: 0.4,
                              flexWrap: 'wrap',
                              maxWidth: 132,
                            }}
                          >
                            {missingProducts.slice(0, 2).map((p) => (
                              <Chip
                                key={p}
                                label={p}
                                size="small"
                                sx={{
                                  fontSize: 9.5,
                                  height: 18,
                                  fontWeight: 500,
                                  bgcolor: 'transparent',
                                  color: colors.success,
                                  border: `1px dashed ${colors.success}66`,
                                  '& .MuiChip-label': { px: 0.75 },
                                }}
                              />
                            ))}
                            {missingProducts.length > 2 && (
                              <Tooltip
                                placement="top"
                                arrow
                                title={
                                  <Box>
                                    <Typography
                                      sx={{
                                        fontSize: 11,
                                        fontWeight: 700,
                                        color: colors.textPrimary,
                                        mb: 0.5,
                                        letterSpacing: '0.04em',
                                        textTransform: 'uppercase',
                                      }}
                                    >
                                      Missing SKUs ({missingProducts.length})
                                    </Typography>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 0.35,
                                      }}
                                    >
                                      {missingProducts.map((p, idx) => (
                                        <Box
                                          key={p}
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.75,
                                            fontSize: 11.5,
                                            color: colors.textSecondary,
                                          }}
                                        >
                                          <Box
                                            sx={{
                                              width: 14,
                                              fontSize: 9.5,
                                              fontWeight: 700,
                                              color: colors.textMuted,
                                              textAlign: 'right',
                                            }}
                                          >
                                            {idx + 1}.
                                          </Box>
                                          {p}
                                        </Box>
                                      ))}
                                    </Box>
                                    <Typography
                                      sx={{
                                        fontSize: 10,
                                        color: colors.textMuted,
                                        mt: 0.75,
                                        fontStyle: 'italic',
                                      }}
                                    >
                                      Best-selling gaps first — top pitch candidates.
                                    </Typography>
                                  </Box>
                                }
                                slotProps={{
                                  tooltip: {
                                    sx: {
                                      bgcolor: '#fff',
                                      border: `1px solid ${colors.border}`,
                                      borderRadius: 1.5,
                                      px: 1.75,
                                      py: 1.25,
                                      boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
                                      maxWidth: 280,
                                    },
                                  },
                                  arrow: {
                                    sx: {
                                      color: '#fff',
                                      '&::before': { border: `1px solid ${colors.border}` },
                                    },
                                  },
                                }}
                              >
                                <Chip
                                  label={`+${missingProducts.length - 2}`}
                                  size="small"
                                  sx={{
                                    fontSize: 9.5,
                                    height: 18,
                                    bgcolor: '#f3f4f6',
                                    color: '#6b7280',
                                    cursor: 'help',
                                    '& .MuiChip-label': { px: 0.75 },
                                    '&:hover': { bgcolor: '#e5e7eb' },
                                  }}
                                />
                              </Tooltip>
                            )}
                            {missingProducts.length === 0 && (
                              <Tooltip
                                placement="top"
                                arrow
                                title="This account carries every SKU in the catalog — no gaps to pitch."
                              >
                                <Box
                                  component="span"
                                  sx={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: 0.4,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    color: colors.success,
                                    cursor: 'help',
                                  }}
                                >
                                  <CheckIcon sx={{ fontSize: 12 }} />
                                  Full range
                                </Box>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Sparkline series={acc.monthly_series} color={currentColor} />
                          {(() => {
                            // Peak month label — names the bar the sparkline
                            // already highlights, so the rep can use it as a
                            // conversation starter ("you did 12 cases in Sep,
                            // what would it take to get back there?") without
                            // hovering.
                            const values = acc.monthly_series.map((p) => Number(p.cases_9l));
                            const max = Math.max(...values, 0);
                            if (max === 0) return null;
                            const idx = values.indexOf(max);
                            const period = acc.monthly_series[idx]?.period;
                            if (!period) return null;
                            return (
                              <Typography
                                sx={{
                                  fontSize: 9.5,
                                  color: colors.textMuted,
                                  fontStyle: 'italic',
                                  mt: 0.4,
                                  letterSpacing: '0.02em',
                                }}
                              >
                                Peak {shortMonth(period)} · {fmt.format(max)}
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell align="right">
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              fontWeight: 700,
                              color: colors.textPrimary,
                              lineHeight: 1.1,
                            }}
                          >
                            {fmt6PkNumber(acc.total_9l)}
                          </Typography>
                          <Typography
                            sx={{
                              fontSize: 10,
                              color: colors.textMuted,
                              mt: 0.15,
                              lineHeight: 1.1,
                              fontFeatureSettings: '"tnum"',
                            }}
                          >
                            {fmt6PkNumber(Number(acc.total_9l))} 6-pk
                          </Typography>
                          {(() => {
                            // Average per active month over the last 12 months —
                            // shows cadence vs depth ("small but regular" vs
                            // "burst buyer"). Sum and count both come from the
                            // 12-month series we already render in the trend col.
                            const values = acc.monthly_series.map((p) => Number(p.cases_9l));
                            const active = values.filter((v) => v > 0);
                            if (active.length === 0) return null;
                            const avg = active.reduce((s, v) => s + v, 0) / active.length;
                            return (
                              <Typography
                                sx={{
                                  fontSize: 9.5,
                                  color: colors.textMuted,
                                  mt: 0.25,
                                  fontStyle: 'italic',
                                  lineHeight: 1,
                                }}
                              >
                                avg {fmt.format(avg)}/mo
                              </Typography>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: '50%',
                                bgcolor: currentColor,
                              }}
                            />
                            <Typography
                              sx={{
                                fontSize: 11,
                                fontWeight: 500,
                                color: colors.textSecondary,
                              }}
                            >
                              {acc.last_active_label}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                fontSize: 9.5,
                                fontWeight: 700,
                                color: currentColor,
                                bgcolor: `${currentColor}14`,
                                px: 0.6,
                                py: 0.1,
                                borderRadius: 0.75,
                                letterSpacing: '0.02em',
                                ml: 0.4,
                                fontFeatureSettings: '"tnum"',
                              }}
                            >
                              {acc.days_since}d
                            </Box>
                          </Box>
                          <VelocityChip
                            pct={acc.velocity_pct}
                            recent={Number(acc.recent_3m_9l)}
                            prior={Number(acc.prior_3m_9l)}
                          />
                        </TableCell>
                        <TableCell>
                          {actionCellOverride ? (
                            actionCellOverride(acc, currentBucket?.label ?? '')
                          ) : (
                            <ActionCell
                              account={acc}
                              flags={flagsByAccount.get(acc.account_id)}
                              bucketAction={suggestedAction(
                                currentBucket?.label ?? '',
                                acc.velocity_pct,
                                Number(acc.recent_3m_9l),
                                Number(acc.prior_3m_9l),
                              )}
                              bucketLabel={currentBucket?.label ?? ''}
                              appendNode={actionCellAppend?.(acc)}
                            />
                          )}
                        </TableCell>
                        {rowActions && (
                          <TableCell
                            className="row-actions"
                            sx={{
                              ...STICKY_ACTIONS_SX,
                              bgcolor: i % 2 === 0 ? '#fff' : '#fafbfc',
                            }}
                          >
                            {rowActions(acc)}
                          </TableCell>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {currentBucket && filtered.length > 0 && (
            <PremiumPagination
              count={filtered.length}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={setPage}
              onRowsPerPageChange={(v) => {
                setRowsPerPage(v);
                setPage(0);
              }}
            />
          )}
        </Box>
      )}

      {/* Reader's guide — explains the bespoke vocabulary used in
          the tracker (pipeline buckets, action flags, tier badges,
          premises classification, action cell). Collapsed by default.
          Surfaces that pass `explainerRows` (Today's Plan) replace the
          default rows wholesale so the guide stays aligned with their
          own chrome instead of the My Territories vocabulary. */}
      <SectionExplainer
        rows={
          explainerRows ?? [
            {
              sample: <FUTSamplePipelineSeg />,
              term: 'Pipeline segments at top',
              desc: (
                <>
                  Each segment is a bucket of accounts grouped by{' '}
                  <strong>days since last order</strong> (0–30, 30–60, 60–90, 90–120, 120+). The
                  brighter slice is the bucket currently in view. Click any segment to drill into
                  who needs attention.
                </>
              ),
            },
            {
              sample: (
                <Box
                  component="span"
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    px: 0.6,
                    py: 0.15,
                    borderRadius: 0.75,
                    color: TIER_STYLE.TOP.color,
                    bgcolor: TIER_STYLE.TOP.bg,
                    border: `1px solid ${TIER_STYLE.TOP.border}`,
                    lineHeight: 1.4,
                  }}
                >
                  TOP
                </Box>
              ),
              term: 'Tier badge (TOP · BIG · MID)',
              desc: (
                <>
                  Ranks the account by <strong>lifetime 6-pk volume within its bucket</strong>.{' '}
                  <strong>TOP</strong> = top 10%, <strong>BIG</strong> = top 25%,{' '}
                  <strong>MID</strong> = top 50%. Bottom 50% deliberately render no badge so each
                  tier stays a meaningful signal.
                </>
              ),
            },
            {
              sample: (
                <Box
                  component="span"
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: '0.08em',
                    px: 0.6,
                    py: 0.15,
                    borderRadius: 0.75,
                    color: colors.info,
                    bgcolor: `${colors.info}14`,
                    border: `1px solid ${colors.info}40`,
                    lineHeight: 1.4,
                  }}
                >
                  OFF
                </Box>
              ),
              term: 'Premises badge (ON · OFF · Online · NA)',
              desc: (
                <>
                  The channel classification. <strong>ON</strong> = on-premise (bars, restaurants),{' '}
                  <strong>OFF</strong> = off-premise (liquor stores, retail),{' '}
                  <strong>Online</strong> = e-commerce / online retailer (a distinct channel, shown
                  in violet), <strong>NA</strong> = neither (clubs, military). Lets you spot the
                  channel without opening the row.
                </>
              ),
            },
            {
              sample: (
                <Box
                  component="span"
                  sx={{
                    fontSize: 9,
                    fontWeight: 800,
                    letterSpacing: '0.4px',
                    textTransform: 'uppercase',
                    px: 0.7,
                    py: 0.3,
                    borderRadius: 1,
                    color: colors.error,
                    bgcolor: `${colors.error}14`,
                    border: `1px solid ${colors.error}40`,
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                  }}
                >
                  RISK
                </Box>
              ),
              term: 'Action chip in the Action column',
              desc: (
                <>
                  The strategic call for this account. <strong>At-risk</strong> /{' '}
                  <strong>Recovery</strong> / <strong>Defend</strong> / <strong>Cross-sell</strong>{' '}
                  / <strong>Nurture</strong>, or a bucket-derived suggestion (<em>Check-in</em>,{' '}
                  <em>Thank-you</em>) when no strategic flag fires. Hover the chip for the rule, the
                  data that fired it, and the suggested next step.
                </>
              ),
            },
            {
              sample: <FUTSampleSparkline />,
              term: '12-month sparkline',
              desc: (
                <>
                  Each bar is one month of 6-pk volume — gives you the cadence at a glance (steady
                  monthly buyer vs burst buyer vs cliff). The rightmost bar is the most recent
                  month.
                </>
              ),
            },
            {
              sample: <FUTSampleMomentum />,
              term: 'Momentum chip (Growing / Declining / Steady)',
              desc: (
                <>
                  Compares the account's <strong>recent 3 months</strong> to the
                  <strong> 3 months before that</strong>. Filter the table by momentum to focus on
                  accounts that are accelerating, sliding, or holding pace.
                </>
              ),
            },
          ]
        }
      />
    </Paper>
  );
}

// ----------------------------------------------------------------
// Sample swatches used inside the FUT SectionExplainer rows. Each
// one mirrors the actual element in the tracker so the rep can map
// the guide to what they see in the table.
// ----------------------------------------------------------------

function FUTSamplePipelineSeg() {
  return (
    <Box sx={{ display: 'flex', height: 18, borderRadius: 0.75, overflow: 'hidden', width: 40 }}>
      <Box sx={{ flex: 1, bgcolor: BUCKET_COLORS['0-30 days'] ?? colors.success, opacity: 0.35 }} />
      <Box
        sx={{ flex: 1, bgcolor: BUCKET_COLORS['30-60 days'] ?? colors.chartPrimary, opacity: 1 }}
      />
      <Box
        sx={{ flex: 1, bgcolor: BUCKET_COLORS['60-90 days'] ?? colors.warning, opacity: 0.35 }}
      />
      <Box sx={{ flex: 1, bgcolor: BUCKET_COLORS['90-120 days'] ?? colors.error, opacity: 0.35 }} />
    </Box>
  );
}

function FUTSampleSparkline() {
  const heights = [6, 9, 4, 12, 8, 14, 10, 16, 11, 18, 14, 20];
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '1.5px', height: 22 }}>
      {heights.map((h, i) => (
        <Box
          key={i}
          sx={{
            width: 2.5,
            height: h,
            bgcolor: colors.chartPrimary,
            opacity: 0.55 + (i / heights.length) * 0.4,
            borderRadius: '1px 1px 0 0',
          }}
        />
      ))}
    </Box>
  );
}

function FUTSampleMomentum() {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.3,
        fontSize: 10,
        fontWeight: 600,
        color: colors.success,
        px: 0.6,
        py: 0.15,
        borderRadius: 0.75,
        bgcolor: `${colors.success}14`,
        border: `1px solid ${colors.success}40`,
      }}
    >
      ↗ +18%
    </Box>
  );
}

// ----------------------------------------------------------------
// Location cell — city/state + distributor + relationship age.
// Hovering the visible content reveals the full mailing address
// (street · city, state zip · county) for the rep to verify the
// venue before a call, drop into a map, or hand off to a territory
// planner.
// ----------------------------------------------------------------

interface LocationCellProps {
  account: FollowUpAccount;
  referenceDate: string;
}

function LocationCell({ account, referenceDate }: LocationCellProps) {
  const cityState = [account.city, account.state_code].filter(Boolean).join(', ') || '—';

  const age =
    account.customer_since && referenceDate
      ? monthsBetween(account.customer_since, referenceDate)
      : null;
  const sinceLabel = account.customer_since_label
    ? `since ${account.customer_since_label}${age !== null ? ` · ${age} mo` : ''}`
    : null;
  const subtitle = [account.distributor_name, sinceLabel].filter(Boolean).join(' · ');

  // Full address layout in the tooltip — street on its own line,
  // city/state/zip on the next, county at the bottom in muted text.
  const cityStateZip = [
    [account.city, account.state_code].filter(Boolean).join(', '),
    account.zip_code,
  ]
    .filter(Boolean)
    .join(' ');
  const hasAddressInfo = Boolean(account.address || account.zip_code || account.county);

  return (
    <Tooltip
      placement="top-start"
      arrow
      enterDelay={250}
      title={
        <Box sx={{ minWidth: 200 }}>
          <Typography
            sx={{
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: colors.textMuted,
              mb: 0.5,
            }}
          >
            Mailing address
          </Typography>
          {hasAddressInfo ? (
            <>
              {account.address && (
                <Typography
                  sx={{
                    fontSize: 12.5,
                    fontWeight: 600,
                    color: colors.textPrimary,
                    lineHeight: 1.35,
                  }}
                >
                  {account.address}
                </Typography>
              )}
              {cityStateZip && (
                <Typography
                  sx={{
                    fontSize: 12,
                    color: colors.textSecondary,
                    lineHeight: 1.35,
                    mt: account.address ? 0.1 : 0,
                  }}
                >
                  {cityStateZip}
                </Typography>
              )}
              {account.county && (
                <Typography sx={{ fontSize: 11, color: colors.textMuted, mt: 0.4 }}>
                  {account.county} county
                </Typography>
              )}
            </>
          ) : (
            <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
              No address on file.
            </Typography>
          )}
          {account.distributor_name && (
            <Typography
              sx={{
                fontSize: 11,
                color: colors.textMuted,
                mt: 0.75,
                pt: 0.75,
                borderTop: `1px solid ${colors.borderLight}`,
              }}
            >
              Distributor · <strong>{account.distributor_name}</strong>
            </Typography>
          )}
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            px: 1.75,
            py: 1.25,
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxWidth: 320,
          },
        },
        arrow: {
          sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } },
        },
      }}
    >
      <Box sx={{ cursor: 'help', display: 'inline-block', minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11.5,
            color: '#9ca3af',
            whiteSpace: 'nowrap',
            borderBottom: hasAddressInfo ? `1px dotted ${colors.textMuted}40` : 'none',
            paddingBottom: '1px',
            display: 'inline-block',
          }}
        >
          {cityState}
        </Typography>
        {subtitle && (
          <Typography
            sx={{
              fontSize: 10,
              color: colors.textMuted,
              whiteSpace: 'nowrap',
            }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>
    </Tooltip>
  );
}

// ----------------------------------------------------------------
// Tier badge — TOP / BIG / MID by lifetime 9L percentile within the
// bucket. Bottom 50% intentionally render nothing so the visual
// signal stays reserved for accounts that actually matter for
// prioritization.
// ----------------------------------------------------------------

interface TierBadgeProps {
  tier: Tier | null;
}

function TierBadge({ tier }: TierBadgeProps) {
  if (!tier) return null;
  const style = TIER_STYLE[tier];
  return (
    <Tooltip
      placement="top"
      arrow
      title={
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: style.color, mb: 0.25 }}>
            Tier · {tier}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.5 }}>
            {style.tip}
          </Typography>
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            px: 1.75,
            py: 1.25,
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxWidth: 280,
          },
        },
        arrow: {
          sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          fontSize: 9,
          fontWeight: 800,
          letterSpacing: '0.08em',
          px: 0.6,
          py: 0.15,
          borderRadius: 0.75,
          color: style.color,
          bgcolor: style.bg,
          border: `1px solid ${style.border}`,
          cursor: 'help',
          flexShrink: 0,
          lineHeight: 1.4,
          transition: 'transform 0.12s',
          '&:hover': { transform: 'translateY(-1px)' },
        }}
      >
        {tier}
      </Box>
    </Tooltip>
  );
}

// ----------------------------------------------------------------
// Compact "label · explanation" row used inside the Action-column
// tooltip key, so all nine action chips read as one scannable list.
// ----------------------------------------------------------------

interface ActionKeyRowProps {
  color: string;
  label: string;
  children: React.ReactNode;
}

function ActionKeyRow({ color, label, children }: ActionKeyRowProps) {
  return (
    <Box
      component="span"
      sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, fontSize: 11.5 }}
    >
      <Box
        component="span"
        sx={{
          display: 'inline-block',
          minWidth: 110,
          flexShrink: 0,
          color,
          fontWeight: 700,
          letterSpacing: '0.01em',
        }}
      >
        {label}
      </Box>
      <Box component="span" sx={{ color: colors.textSecondary, lineHeight: 1.55 }}>
        — {children}
      </Box>
    </Box>
  );
}

// ----------------------------------------------------------------
// Column-header cell with a hover tooltip that explains in detail
// what the column's values represent. Uppercase 10px label matches
// the MVP table style; the cursor: help signals hoverability.
// ----------------------------------------------------------------

interface ColumnHeaderProps {
  label: string;
  title: string;
  desc: React.ReactNode;
  width?: number;
  align?: 'left' | 'right';
  tooltipMaxWidth?: number;
  /** Pin this header cell to the right edge (rep-actions column). */
  stickyRight?: boolean;
}

/**
 * Pins the rep-actions column to the right edge of the scrolling table so
 * Log Visit stays in reach at any width (the table is ~1,400px wide; on a
 * laptop or phone the column would otherwise sit off-screen). Needs an
 * opaque background so scrolled cells don't show through.
 */
const STICKY_ACTIONS_SX = {
  position: 'sticky',
  right: 0,
  zIndex: 1,
  boxShadow: '-8px 0 8px -8px rgba(15, 23, 42, 0.22)',
} as const;

function ColumnHeader({
  label,
  title,
  desc,
  width,
  align = 'left',
  tooltipMaxWidth = 340,
  stickyRight = false,
}: ColumnHeaderProps) {
  return (
    <TableCell
      align={align}
      sx={{
        width,
        // stickyHeader already pins the row to the top; z-index lifts the
        // corner cell above both the header row and pinned body cells.
        ...(stickyRight ? { ...STICKY_ACTIONS_SX, zIndex: 3 } : {}),
      }}
    >
      <Tooltip
        placement="top"
        arrow
        title={
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary, mb: 0.5 }}>
              {title}
            </Typography>
            <Typography
              sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.55 }}
              component="div"
            >
              {desc}
            </Typography>
          </Box>
        }
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: '#fff',
              border: `1px solid ${colors.border}`,
              borderRadius: 1.5,
              px: 2,
              py: 1.5,
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              maxWidth: tooltipMaxWidth,
            },
          },
          arrow: {
            sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } },
          },
        }}
      >
        {/* The actual rendered header label. The dotted underline + help
            cursor signals "hover me for context." */}
        <Box
          component="span"
          sx={{
            cursor: 'help',
            borderBottom: `1px dotted ${colors.textMuted}80`,
            display: 'inline-block',
            paddingBottom: '1px',
          }}
        >
          {label}
        </Box>
      </Tooltip>
    </TableCell>
  );
}

// ----------------------------------------------------------------
// Per-row 12-month activity strip.
//
// Design intent — replaces the original "sparkline" which was too thin
// to read at table density:
//   * Bars are 9px wide instead of 4px so they're individually legible
//     and obviously bars (not dots).
//   * A pale baseline rail sits behind every bar so zero months still
//     have visual presence — they read as "we have data, the account
//     didn't move product" rather than blank space.
//   * The peak month renders at full opacity in the bucket color;
//     other active months use a calm tint. The eye locks onto the
//     peak as a focal point.
//   * A tiny axis below shows just the first and last month so the
//     timeline orients without crowding (Stripe/Linear style).
//   * Each bar has a hover tooltip with exact month, volume, and a
//     "Peak month" badge where applicable.
// ----------------------------------------------------------------

interface SparklineProps {
  series: FollowUpMonthlyVolume[];
  color: string;
}

const BAR_W = 9;
const BAR_GAP = 3;
const BAR_H = 30;
const ZERO_RAIL_H = 2;

function Sparkline({ series, color }: SparklineProps) {
  const values = series.map((p) => Number(p.cases_9l));
  const max = Math.max(...values, 0);
  const peakIdx = max > 0 ? values.indexOf(max) : -1;

  const firstLabel = series.length > 0 ? formatMonthShort(series[0]!.period) : '';
  const lastLabel = series.length > 0 ? formatMonthShort(series[series.length - 1]!.period) : '';
  // Bar strip width — used to align the month-range labels under it.
  const stripW = values.length * BAR_W + (values.length - 1) * BAR_GAP;

  return (
    <Box sx={{ display: 'inline-block' }}>
      {/* Bars */}
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-end',
          gap: `${BAR_GAP}px`,
          height: BAR_H,
          position: 'relative',
          // Faint horizontal baseline behind the bars — anchors the
          // strip and gives zero months a visual home.
          '&::after': {
            content: '""',
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: 0,
            height: '1px',
            bgcolor: colors.borderLight,
            zIndex: 0,
          },
        }}
      >
        {values.map((v, i) => {
          const isZero = v === 0;
          const h = max > 0 && v > 0 ? Math.max((v / max) * BAR_H, 4) : ZERO_RAIL_H;
          const isPeak = !isZero && i === peakIdx;
          const period = series[i]?.period ?? '';
          return (
            <Tooltip
              key={period}
              placement="top"
              arrow
              title={
                <Box>
                  <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>
                    {formatMonth(period)}
                  </Typography>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: colors.textPrimary }}>
                    {isZero ? 'No depletions' : `${fmt6PkNumber(v)} 6-pk`}
                  </Typography>
                  {isPeak && (
                    <Typography sx={{ fontSize: 10, color: colors.success, fontWeight: 600 }}>
                      Peak month
                    </Typography>
                  )}
                </Box>
              }
              slotProps={{
                tooltip: {
                  sx: {
                    bgcolor: '#fff',
                    border: `1px solid ${colors.border}`,
                    borderRadius: 1,
                    px: 1.25,
                    py: 0.75,
                    boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
                  },
                },
                arrow: {
                  sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } },
                },
              }}
            >
              <Box
                sx={{
                  width: BAR_W,
                  height: h,
                  bgcolor: isZero ? colors.borderLight : color,
                  // Peak month full intensity; other active months calmer.
                  opacity: isZero ? 1 : isPeak ? 0.95 : 0.45,
                  borderRadius: '2px 2px 0 0',
                  transition: 'opacity 0.15s, transform 0.15s',
                  cursor: 'pointer',
                  zIndex: 1,
                  '&:hover': {
                    opacity: isZero ? 1 : 0.95,
                    transform: isZero ? 'none' : 'scaleY(1.08)',
                    transformOrigin: 'bottom',
                  },
                }}
              />
            </Tooltip>
          );
        })}
      </Box>

      {/* Tiny month-range axis — start and end labels only, like Stripe. */}
      <Box
        sx={{
          mt: 0.4,
          display: 'flex',
          justifyContent: 'space-between',
          width: stripW,
        }}
      >
        <Typography
          sx={{
            fontSize: 9,
            color: colors.textMuted,
            lineHeight: 1,
            letterSpacing: '0.04em',
            fontWeight: 500,
          }}
        >
          {firstLabel}
        </Typography>
        <Typography
          sx={{
            fontSize: 9,
            color: colors.textMuted,
            lineHeight: 1,
            letterSpacing: '0.04em',
            fontWeight: 500,
          }}
        >
          {lastLabel}
        </Typography>
      </Box>
    </Box>
  );
}

const _MONTH_NAMES = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
];

function formatMonth(iso: string): string {
  // ISO YYYY-MM-DD → "Mar 2026"
  const [y, m] = iso.split('-');
  const idx = m ? Number(m) - 1 : 0;
  return `${_MONTH_NAMES[idx] ?? '?'} ${y ?? ''}`;
}

function formatMonthShort(iso: string): string {
  // ISO YYYY-MM-DD → "Mar '26"
  const [y, m] = iso.split('-');
  const idx = m ? Number(m) - 1 : 0;
  return `${_MONTH_NAMES[idx] ?? '?'} '${(y ?? '').slice(-2)}`;
}

// ----------------------------------------------------------------
// Velocity chip: last-3-months vs prior-3-months direction.
// Green ▲ for growth, red ▼ for decline, grey ≈ for steady, indigo for
// "New activity" (no prior baseline). Sits under the Last Order month.
// ----------------------------------------------------------------

interface VelocityChipProps {
  pct: number | null;
  recent: number;
  prior: number;
}

function VelocityChip({ pct, recent, prior }: VelocityChipProps) {
  if (prior === 0 && recent === 0) {
    return null;
  }

  // NEW ACTIVITY — no prior baseline. Either a brand-new account or one
  // that went fully silent and just came back.
  if (prior === 0 && recent > 0) {
    return (
      <VelocityBadge
        text="NEW ACTIVITY"
        bg={`${colors.chartPrimary}14`}
        fg={colors.chartPrimary}
        tooltipTitle="New Activity"
        tooltipBody={
          <>
            This account had <strong>no recorded depletions</strong> in the 3 months prior to the
            most recent 3-month window, but is moving product now ({fmt6PkNumber(recent)} 6-pk).
            <br />
            <br />
            That means one of two things, both worth investigating:
            <ul style={{ margin: '4px 0', paddingLeft: 18 }}>
              <li>A brand-new account that just placed its first order.</li>
              <li>A dormant account that came back to life — a re-engagement win.</li>
            </ul>
            No prior baseline = no percentage growth number to show — the chip flags the situation
            instead. Nurture the relationship while it&apos;s fresh.
          </>
        }
      />
    );
  }
  if (pct === null) return null;

  // STEADY — within ±10% of the prior 3 months.
  if (Math.abs(pct) < 10) {
    return (
      <VelocityBadge
        text="≈ STEADY"
        bg={colors.surfaceSubtle}
        fg={colors.textMuted}
        tooltipTitle="Steady cadence"
        tooltipBody={
          <>
            Recent 3 months ({fmt6PkNumber(recent)} 6-pk) is within <strong>±10%</strong> of the
            prior 3 months ({fmt6PkNumber(prior)} 6-pk) — change of{' '}
            <strong>
              {pct > 0 ? '+' : ''}
              {pct.toFixed(1)}%
            </strong>
            .
            <br />
            <br />
            The account is holding pattern. Not growing, not slipping — keep them on the regular
            check-in cadence.
          </>
        }
      />
    );
  }

  const up = pct > 0;
  const accent = up ? colors.success : colors.error;
  return (
    <VelocityBadge
      text={`${up ? '▲' : '▼'} ${up ? '+' : ''}${pct.toFixed(0)}%`}
      bg={`${accent}14`}
      fg={accent}
      tooltipTitle={up ? 'Growing' : 'Declining'}
      tooltipBody={
        <>
          Recent 3 months: <strong>{fmt6PkNumber(recent)} 6-pk</strong>
          <br />
          Prior 3 months: <strong>{fmt6PkNumber(prior)} 6-pk</strong>
          <br />
          Change:{' '}
          <strong style={{ color: accent }}>
            {up ? '+' : ''}
            {pct.toFixed(1)}%
          </strong>
          <br />
          <br />
          {up
            ? 'Volume is up versus the prior period. Lean in — keep the momentum going with reorders, cross-sell, or trial of an additional SKU.'
            : 'Volume is down versus the prior period. Worth a call to understand what changed — supply gap? price? competing brand?'}
        </>
      }
    />
  );
}

interface VelocityBadgeProps {
  text: string;
  bg: string;
  fg: string;
  tooltipTitle: string;
  tooltipBody: React.ReactNode;
}

function VelocityBadge({ text, bg, fg, tooltipTitle, tooltipBody }: VelocityBadgeProps) {
  return (
    <Tooltip
      placement="left"
      arrow
      title={
        <Box>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: fg, mb: 0.5 }}>
            {tooltipTitle}
          </Typography>
          <Typography
            sx={{ fontSize: 11.5, color: colors.textSecondary, lineHeight: 1.55 }}
            component="div"
          >
            {tooltipBody}
          </Typography>
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            px: 2,
            py: 1.5,
            boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
            maxWidth: 320,
          },
        },
        arrow: {
          sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } },
        },
      }}
    >
      <Box
        component="span"
        sx={{
          mt: 0.4,
          display: 'inline-block',
          px: 0.85,
          py: 0.15,
          fontSize: 9.5,
          fontWeight: 700,
          letterSpacing: '0.04em',
          borderRadius: 0.75,
          bgcolor: bg,
          color: fg,
          cursor: 'help',
          transition: 'transform 0.12s',
          '&:hover': { transform: 'translateY(-1px)' },
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}

// ----------------------------------------------------------------
// ActionCell — single, consolidated Action surface. Replaces the
// old combo of (action chips next to name) + (small bucket-action
// chip in this column). One column now answers "what should I do
// with this account, why, and how was that decided" via:
//   * a primary action chip (the most urgent strategic flag, or the
//     bucket-derived fallback if none fire)
//   * a one-line specific-to-this-account reason
//   * secondary chip(s) for any other triggered flags
//   * a rich hover tooltip explaining the rule, the data that fired
//     it, and the suggested next step
// ----------------------------------------------------------------

// Action priority — most urgent first. Determines which flag is the
// "primary" shown in the chip + reason line.
const ACTION_PRIORITY: ActionFlag[] = [
  'core_gap',
  'at_risk',
  'recovery',
  'defend',
  'mono_sku',
  'fresh',
];

interface ActionDetail {
  label: string;
  color: string;
  bg: string;
  // Rule expressed plainly so the rep can audit how the flag fired.
  rule: string;
  // Concrete numbers for THIS account that triggered the rule.
  triggerLine: (a: FollowUpAccount, derived: ActionDerivations) => string;
  // Citation: where the system pulled the inputs from + the raw
  // values it consumed. Lets the rep verify the call themselves.
  cite: (a: FollowUpAccount, derived: ActionDerivations) => ActionCitation;
  // What to actually do about it.
  suggestedStep: string;
}

interface ActionCitation {
  source: string;
  values: string;
}

interface ActionDerivations {
  recent: number;
  prior: number;
  longestStreak: number;
  currentStreak: number;
  activeMonths: number;
  recentWindow: string; // e.g. "Apr '26 – Jun '26"
  priorWindow: string; // e.g. "Jan '26 – Mar '26"
}

const ACTION_DETAIL: Record<ActionFlag, ActionDetail> = {
  core_gap: {
    label: 'No Rye/Bourbon',
    color: CORE_GAP_TONE,
    bg: `${CORE_GAP_TONE}14`,
    rule: "Account doesn't carry both a Rye and a Bourbon (Bottled in Bond). Missing either flags it.",
    triggerLine: (a) => {
      const missing: string[] = [];
      if (!a.products.some((p) => p.toLowerCase().startsWith('rye'))) missing.push('Rye');
      if (!a.products.some((p) => p.toLowerCase().startsWith('bottled in b')))
        missing.push('Bourbon');
      return `Missing ${missing.join(' + ')} · carries ${a.products.length || 'no'} SKU${a.products.length === 1 ? '' : 's'}`;
    },
    cite: (a) => ({
      source: `products list · lifetime`,
      values: `carries: ${a.products.length ? a.products.join(', ') : '(none)'}`,
    }),
    suggestedStep:
      'Pitch the core whiskey line — lead with whichever of Rye / Bourbon they’re missing to round out the shelf.',
  },
  at_risk: {
    label: 'At-risk',
    color: colors.error,
    bg: `${colors.error}14`,
    rule: 'Was a near-monthly buyer (≥9 of last 12 months active) AND recent 3M dropped sharply vs prior 3M (≥30% drop or recent < 50% of prior).',
    triggerLine: (a, d) =>
      a.velocity_pct !== null
        ? `Recent 3M ${a.velocity_pct.toFixed(0)}% vs prior · ${fmt6PkNumber(d.recent)} vs ${fmt6PkNumber(d.prior)} 6-pk`
        : `Recent 3M ${fmt6PkNumber(d.recent)} 6-pk vs prior 3M ${fmt6PkNumber(d.prior)} 6-pk`,
    cite: (a, d) => ({
      source: `depletions ledger · ${d.recentWindow} vs ${d.priorWindow}`,
      values: `active ${d.activeMonths}/12 mo · prior 3M = ${fmt6PkNumber(d.prior)} → recent 3M = ${fmt6PkNumber(d.recent)} 6-pk${a.velocity_pct !== null ? ` · velocity ${a.velocity_pct.toFixed(0)}%` : ''}`,
    }),
    suggestedStep:
      'Schedule a check-in this week. Find out what changed before they go fully dark.',
  },
  recovery: {
    label: 'Recovery',
    color: colors.warning,
    bg: `${colors.warning}14`,
    rule: 'Had at least one ≥4-month consecutive streak in the visible window AND is currently silent (zero in the latest cell).',
    triggerLine: (a, d) =>
      `${d.longestStreak}-month streak in this window · silent for ${a.days_since} days`,
    cite: (a, d) => ({
      source: `depletions ledger · 12-month monthly_series`,
      values: `longest streak = ${d.longestStreak} mo · silent ${a.days_since} days · last order ${a.last_active_label}`,
    }),
    suggestedStep:
      'Reach out warmly — share what is new since they were last active. Reconnect before the relationship cools.',
  },
  defend: {
    label: 'Defend',
    color: colors.chartPrimary,
    bg: `${colors.chartPrimary}14`,
    rule: 'Recent 3M is up sharply vs prior 3M (≥30% growth or recent ≥ 1.5× prior) with both windows ≥5 6-pk baseline.',
    triggerLine: (a, d) =>
      a.velocity_pct !== null
        ? `Recent 3M +${a.velocity_pct.toFixed(0)}% vs prior · ${fmt6PkNumber(d.recent)} vs ${fmt6PkNumber(d.prior)} 6-pk`
        : `Recent 3M ${fmt6PkNumber(d.recent)} 6-pk vs prior 3M ${fmt6PkNumber(d.prior)} 6-pk`,
    cite: (a, d) => ({
      source: `depletions ledger · ${d.recentWindow} vs ${d.priorWindow}`,
      values: `prior 3M = ${fmt6PkNumber(d.prior)} → recent 3M = ${fmt6PkNumber(d.recent)} 6-pk${a.velocity_pct !== null ? ` · velocity +${a.velocity_pct.toFixed(0)}%` : ''}`,
    }),
    suggestedStep:
      'Lock in supply for the next quarter while momentum is strong. Confirm inventory so the next order does not stall.',
  },
  mono_sku: {
    label: 'Cross-sell',
    color: colors.info,
    bg: `${colors.info}14`,
    rule: 'Account has bought exactly one distinct SKU across all history.',
    triggerLine: (a) =>
      `100% ${a.products[0] ?? 'SKU'} · ${fmt6PkNumber(Number(a.total_9l))} 6-pk lifetime`,
    cite: (a) => ({
      source: `products list · lifetime`,
      values: `SKU count = 1 (${a.products[0] ?? 'unknown'}) · total = ${fmt6PkNumber(Number(a.total_9l))} 6-pk lifetime`,
    }),
    suggestedStep:
      'Pitch a complementary SKU on the next call. They already have a buying habit — give them a second reason.',
  },
  fresh: {
    label: 'Nurture',
    color: colors.success,
    bg: `${colors.success}14`,
    rule: 'First non-zero month for this account is within the last 90 days of the reference date.',
    triggerLine: (a) =>
      a.customer_since_label
        ? `Customer since ${a.customer_since_label} · ${fmt6PkNumber(Number(a.total_9l))} 6-pk so far`
        : 'First active in the last 90 days',
    cite: (a) => ({
      source: `account header · customer_since`,
      values: `customer since ${a.customer_since_label ?? 'recent'} · total = ${fmt6PkNumber(Number(a.total_9l))} 6-pk · last order ${a.last_active_label}`,
    }),
    suggestedStep:
      'Send a welcome with product info + suggested next SKUs. Lock in the buying habit before it fades.',
  },
};

interface ActionCellProps {
  account: FollowUpAccount;
  flags: AccountActionFlags | undefined;
  bucketAction: SuggestedAction;
  bucketLabel: string;
  /** Optional content appended below the system-generated tier
   *  badge + explanation.  Used by the Today's Plan surface to fold
   *  proximity / reason / last-visit chips into the same column so
   *  the rep sees one consolidated "system says…" block instead of
   *  hunting across two columns. */
  appendNode?: ReactNode;
}

function ActionCell({ account, flags, bucketAction, bucketLabel, appendNode }: ActionCellProps) {
  // Compute derivations once per row so trigger lines + tooltip share them.
  const vals = account.monthly_series.map((p) => Number(p.cases_9l));
  let longestStreak = 0;
  let running = 0;
  let activeMonths = 0;
  for (const v of vals) {
    if (v > 0) {
      running++;
      activeMonths++;
      if (running > longestStreak) longestStreak = running;
    } else running = 0;
  }
  let currentStreak = 0;
  for (let i = vals.length - 1; i >= 0; i--) {
    if (vals[i]! > 0) currentStreak++;
    else break;
  }
  // Build human window labels from monthly_series so the citation
  // names the exact months the rule consumed (e.g. "Apr '26 – Jun '26").
  const series = account.monthly_series;
  const lastIdx = series.length - 1;
  const recentWindow =
    series.length >= 3
      ? `${shortMonth(series[lastIdx - 2]!.period)} – ${shortMonth(series[lastIdx]!.period)}`
      : 'recent 3M';
  const priorWindow =
    series.length >= 6
      ? `${shortMonth(series[lastIdx - 5]!.period)} – ${shortMonth(series[lastIdx - 3]!.period)}`
      : 'prior 3M';
  const derived: ActionDerivations = {
    recent: Number(account.recent_3m_9l),
    prior: Number(account.prior_3m_9l),
    longestStreak,
    currentStreak,
    activeMonths,
    recentWindow,
    priorWindow,
  };

  const activeFlags: ActionFlag[] = flags ? ACTION_PRIORITY.filter((f) => flags[f]) : [];
  const primary = activeFlags[0] ?? null;
  const secondary = activeFlags.slice(1);

  const primaryLabel = primary ? ACTION_DETAIL[primary].label : bucketAction.label;
  const primaryColor = primary ? ACTION_DETAIL[primary].color : bucketAction.color;
  const primaryBg = primary ? ACTION_DETAIL[primary].bg : bucketAction.bg;
  const reasonLine = primary
    ? ACTION_DETAIL[primary].triggerLine(account, derived)
    : bucketAction.why;
  const suggestedStep = primary ? ACTION_DETAIL[primary].suggestedStep : bucketAction.why;

  return (
    <Tooltip
      placement="left-start"
      arrow
      enterDelay={150}
      title={
        <Box sx={{ p: 0.25, maxWidth: 360 }}>
          <Typography sx={{ fontSize: 12.5, fontWeight: 800, color: colors.textPrimary, mb: 0.3 }}>
            {account.name}
          </Typography>
          <Typography
            sx={{ fontSize: 10.5, color: colors.textMuted, mb: 0.75, letterSpacing: '0.04em' }}
          >
            Bucket · {bucketLabel} · {bucketAction.label.toLowerCase()}
          </Typography>

          {/* Primary action explainer */}
          <Box
            sx={{
              borderTop: `1px solid ${colors.borderLight}`,
              pt: 0.75,
              mb: secondary.length > 0 ? 0.75 : 0,
            }}
          >
            <Typography
              sx={{
                fontSize: 10,
                fontWeight: 700,
                letterSpacing: '0.08em',
                textTransform: 'uppercase',
                color: colors.textMuted,
                mb: 0.4,
              }}
            >
              Recommended action
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: primaryColor, mb: 0.4 }}>
              {primaryLabel}
            </Typography>
            {primary ? (
              <ActionExplainerBlock
                rule={ACTION_DETAIL[primary].rule}
                trigger={reasonLine}
                suggested={suggestedStep}
                citation={ACTION_DETAIL[primary].cite(account, derived)}
                color={primaryColor}
              />
            ) : (
              <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.5 }}>
                {bucketAction.why}
              </Typography>
            )}
          </Box>

          {/* Secondary signals */}
          {secondary.length > 0 && (
            <Box
              sx={{
                borderTop: `1px solid ${colors.borderLight}`,
                pt: 0.75,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
              }}
            >
              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                }}
              >
                Also flagged
              </Typography>
              {secondary.map((flag) => {
                const d = ACTION_DETAIL[flag];
                return (
                  <Box key={flag}>
                    <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: d.color, mb: 0.2 }}>
                      {d.label}
                    </Typography>
                    <ActionExplainerBlock
                      rule={d.rule}
                      trigger={d.triggerLine(account, derived)}
                      suggested={d.suggestedStep}
                      citation={d.cite(account, derived)}
                      color={d.color}
                    />
                  </Box>
                );
              })}
            </Box>
          )}
        </Box>
      }
      slotProps={{
        tooltip: {
          sx: {
            bgcolor: '#fff',
            color: colors.textPrimary,
            border: `1px solid ${colors.border}`,
            borderRadius: 1.5,
            px: 1.5,
            py: 1.25,
            boxShadow: '0 12px 32px rgba(15,23,42,0.12), 0 2px 6px rgba(15,23,42,0.05)',
            maxWidth: 380,
            fontSize: 11.5,
            lineHeight: 1.45,
          },
        },
        arrow: { sx: { color: '#fff', '&::before': { border: `1px solid ${colors.border}` } } },
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.4 }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.4,
            px: 0.9,
            py: 0.35,
            fontSize: 10.5,
            fontWeight: 800,
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            borderRadius: 1,
            bgcolor: primaryBg,
            color: primaryColor,
            border: `1px solid ${primaryColor}40`,
            cursor: 'help',
            whiteSpace: 'nowrap',
          }}
        >
          {primaryLabel}
        </Box>
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textSecondary,
            lineHeight: 1.3,
            maxWidth: 220,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {reasonLine}
        </Typography>
        {secondary.length > 0 && (
          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, flexWrap: 'wrap' }}>
            {secondary.slice(0, 2).map((flag) => {
              const d = ACTION_DETAIL[flag];
              return (
                <Box
                  key={flag}
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    px: 0.45,
                    py: 0.1,
                    fontSize: 8.5,
                    fontWeight: 700,
                    letterSpacing: 0.3,
                    textTransform: 'uppercase',
                    borderRadius: 0.5,
                    bgcolor: d.bg,
                    color: d.color,
                    border: `1px solid ${d.color}33`,
                  }}
                >
                  + {d.label}
                </Box>
              );
            })}
            {secondary.length > 2 && (
              <Typography
                sx={{
                  fontSize: 9,
                  color: colors.textMuted,
                  fontWeight: 700,
                  fontFeatureSettings: '"tnum"',
                }}
              >
                +{secondary.length - 2} more
              </Typography>
            )}
          </Box>
        )}
        {appendNode}
      </Box>
    </Tooltip>
  );
}

// Mini explainer block — Rule / This account / Based on / Suggested
// quadruple used inside the ActionCell tooltip for each flag.
// "Based on" is the citation row that names the source + raw values
// so the rep can audit how the system arrived at this call.
interface ActionExplainerBlockProps {
  rule: string;
  trigger: string;
  suggested: string;
  citation: ActionCitation;
  color: string;
}

function ActionExplainerBlock({
  rule,
  trigger,
  suggested,
  citation,
  color,
}: ActionExplainerBlockProps) {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          Rule
        </Typography>
        <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>
          {rule}
        </Typography>
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          This account
        </Typography>
        <Typography
          sx={{
            fontSize: 11,
            color,
            fontWeight: 700,
            lineHeight: 1.4,
            fontFeatureSettings: '"tnum"',
          }}
        >
          {trigger}
        </Typography>
      </Box>
      <Box
        sx={{
          bgcolor: colors.surfaceSubtle,
          border: `1px dashed ${colors.borderLight}`,
          borderRadius: 0.75,
          px: 0.75,
          py: 0.5,
        }}
      >
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          Based on
        </Typography>
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textMuted,
            lineHeight: 1.35,
            fontStyle: 'italic',
          }}
        >
          {citation.source}
        </Typography>
        <Typography
          sx={{
            fontSize: 10.5,
            color: colors.textSecondary,
            lineHeight: 1.35,
            fontFeatureSettings: '"tnum"',
            mt: 0.15,
          }}
        >
          {citation.values}
        </Typography>
      </Box>
      <Box>
        <Typography
          sx={{
            fontSize: 9.5,
            fontWeight: 700,
            letterSpacing: '0.05em',
            textTransform: 'uppercase',
            color: colors.textMuted,
          }}
        >
          Suggested
        </Typography>
        <Typography sx={{ fontSize: 11, color: colors.textSecondary, lineHeight: 1.4 }}>
          {suggested}
        </Typography>
      </Box>
    </Box>
  );
}
